import * as THREE from 'three';
import { clamp, hash01, hash3, lerp, smoothstep, valueNoise } from '@/core/rng';
import { BIOMES, type Biome, type BiomeKey } from '@/world/types';
import { dryness } from '@/render/season';
// The lattice, the hashes and the biome table are world content, not
// rendering, and since Design III §28 the pointer needs them too — so they
// live in `world/litter.ts` and this module draws what that module decides.
// `litterCell` in particular is shared, so what you point at and what is drawn
// cannot drift apart.
import {
  STEP, spotX, spotZ, litterCell, litterSize, specialise, wornKeep, SPEC, FALLBACK,
  A_MAX, BOOST, LOG_CHANCE, DRY_FOOT, MODEL, POINTABLE_FAR, KIND_COUNT, KIND_NAME,
  K_STONE, K_STICK, K_CLUMP, K_LOG,
  K_SHELL, K_BONE, K_FERN, K_FLOWER, K_MUSHROOM, K_MOSS, K_SEEDHEAD,
  S_OCC, S_KIND, S_LOG, S_SZ, S_SH, S_ROT, S_TONE,
} from '@/world/litter';

/**
 * Near detail — the sub-metre litter of a real landscape.
 *
 * The camera opens 340 m up and the ten-to-two-hundred-metre band held nothing:
 * terrain is a height field on 4 m vertices with flat vertex colours, and the
 * smallest thing chunk vegetation ever grows is a 0.4 m grass tuft. Close ground
 * therefore read as smooth coloured dough with lollipops standing on it. Nothing
 * in that band told the eye how big a metre is.
 *
 * This layer fills it with the things that live under the ankle and that chunk
 * generation deliberately never makes: pebbles and small stones, twigs and
 * deadfall sticks, small sedge and grass clumps, and the occasional fallen log —
 * plus the seven things a particular patch of ground is *known for*: shells on
 * the strand, bones in dry country, ferns and mushrooms and moss under a
 * canopy, flowers and seed heads in the open. Eleven low-poly shapes, one
 * InstancedMesh each.
 *
 * The seven specials cost nothing extra to draw in the sense that matters:
 * `world/litter.ts` decides occupancy first and the speciality afterwards, so a
 * mushroom stands exactly where a clump would have and the budget, the caps and
 * every biome's total are untouched by the new vocabulary. What they buy is
 * that a floor is no longer one shape at eleven sizes — `tools/litter.ts`
 * measured a coast forest as 80% one clump silhouette before this, and that is
 * what "spam" turned out to mean when it was finally printed.
 *
 * ## Camera-local, world-anchored
 *
 * Unlike `rocks.ts` (built once for the whole island) or chunk vegetation (built
 * per chunk), this is a *camera-local* layer: a dense disc of a few hundred
 * metres that travels with the focus point, and a sparse landmark band — logs,
 * fists of stone, the broadest clumps — reaching a kilometre beyond it. That is
 * the only affordable way to put a pebble every eight square metres anywhere on
 * a 50 km island and still let its biggest litter read from the middle distance.
 *
 * The trap in a moving layer is drift. It is avoided by never owning any
 * positions: candidates come from a **fixed world lattice** of 2 m cells, and
 * every property of a cell's item — where it stands inside the cell, what it is,
 * how big, which way round, what colour — is a hash of the *cell's integer
 * coordinates* alone. The camera chooses which cells to draw and nothing else.
 * So as the disc moves, items leave one edge and appear at the other because the
 * lattice is world-anchored, not because anything moved: a stone that was at
 * (412.7, −88.3) is at (412.7, −88.3) on the next frame, the next session and
 * every other machine. Nothing swims, crawls or shimmers, and the only camera
 * quantity that reaches the placement is a *count* — how many of the candidates
 * the budget can afford, which only ever adds or removes whole items, never
 * moves one.
 *
 * Items appear and vanish by scaling to zero — at the rim of the disc, and at
 * the budget threshold when the disc grows — so nothing ever pops.
 *
 * Render-only: it writes no world state, the simulation cannot see it, and its
 * one time input is the `tSec` handed to `update` (the grass sway, in the
 * shader). Zero allocation on the per-frame path.
 */

export interface NearDetail {
  group: THREE.Group;
  /** Called each frame with the focus point, camera orbit radius, and ground sampler. */
  update(tSec: number, focusX: number, focusZ: number, camR: number,
         groundAt: (x: number, z: number) => number,
         siteAt: (x: number, z: number) => { land: boolean; biome: string; rain: number },
         /**
          * Places whose item a god has carried off (Design IV §21). The
          * lattice grows a stone at that cell for ever; this is the record
          * that it is no longer lying there. Shaped exactly like `sparing()`
          * — one yes/no about a place — and `undefined` where nothing has
          * ever been moved, which is the usual case and costs nothing.
          */
         displaced?: (x: number, z: number) => boolean,
         /**
          * Bumps whenever that answer could have changed. The disc caches its
          * cells by cell index, so a stone picked up without the focus moving
          * would otherwise stay on screen until the player walked away.
          */
         worldGen?: number,
         /** Things a god moved, to be drawn where they now lie. */
         carried?: ReadonlyArray<{ kind: string; x: number; z: number; size: number }>,
         /**
          * How packed the ground is by living on it, 0..1 — `wear.ts`'s own
          * reading. Sweeps the grass carpet and thins the living clumps where
          * people live; dead debris stays, because camps have debris. Wear
          * moves on day scales, so the caller folds the sim day into
          * `worldGen` and the cache re-asks once a day rather than per frame.
          */
         worn?: (x: number, z: number) => number,
         /**
          * Is this point under drawn water (`render/rivers.ts`'s own record
          * of its ribbons)? Read once per cell: no grass carpet and no flora
          * stand in a river. Renderer-to-renderer knowledge about a drawn
          * thing — the world is not asked, and litter (stones, sticks) still
          * lies in a bed, as it does.
          */
         water?: (x: number, z: number) => boolean): void;
  /**
   * Live multipliers from the settings menu: `reach` scales the disc, the
   * landmark band, the layer's off-ceiling and the litter budget together;
   * `grass` scales the carpet's radius, fade range and blade budget. Both
   * default 1; 0 switches the respective half off. Forces a full rebuild on
   * change, so the next update() reflects it — the buffers were allocated
   * for the shipped caps and every fill site already clamps against them.
   */
  /**
   * Is the litter item in this cell DRAWN right now — by the disc, the
   * landmark band, or as something carried? The pointer asks this before it
   * names a stone or a clump: `world/litter.ts` says what the ground holds,
   * and until 2026-08-18 the pointer named all of it inside `POINTABLE_FAR`,
   * so a clump of sedge the budget was omitting at 95 m could be selected and
   * not seen. Existence is camera-free; being pointable is being visible.
   */
  isDrawn(x: number, z: number): boolean;
  setScale(reach: number, grass: number): void;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// The lattice — its constants and hashes live in `world/litter.ts`
// ---------------------------------------------------------------------------

const TAU = Math.PI * 2;

/**
 * kind name → the mesh that draws it. Built from `KIND_NAME` rather than
 * written out, so a kind added to the world cannot be forgotten here — the old
 * hand-written table would silently have drawn every new kind as a pebble,
 * which is the shape of bug that survives review because it *renders*.
 */
const KIND_INDEX: Record<string, number> = {};
KIND_NAME.forEach((k, i) => { KIND_INDEX[k] = i; });

/**
 * The one cell of each SUPER×SUPER super-cell that the landmark band may
 * draw, hashed from the super-cell's own coordinates. A pure function of
 * position like everything else on the lattice — the same super-cell elects
 * the same cell on every machine, for ever.
 */
const S_REP = 0xa3d7;
const repX = (sx: number, sz: number): number =>
  sx * SUPER + (hash3(sx, sz, S_REP) & (SUPER - 1));
const repZ = (sx: number, sz: number): number =>
  sz * SUPER + ((hash3(sx, sz, S_REP) >>> 2) & (SUPER - 1));

/**
 * The size the landmark band judges an item by — `world/litter.ts`'s own
 * `litterSize`, which is now the single copy of that arithmetic. It used to be
 * duplicated here as `farSize`, and two copies of a formula that must agree
 * exactly is a bug with a delay fuse: the day one of them was tuned, the band
 * would have drawn a different log from the one the pointer named.
 *
 * For a log the number is its *length*: an eight-metre trunk reads as a line
 * long after its diameter is gone.
 */
const farSize = litterSize;

/**
 * Would the landmark band ever own this item? A log always; a stone or a
 * clump only above a flat size floor; a stick never — a one-pixel bright line
 * at range is confetti, which is the lesson this layer's palette already
 * learned once. Ownership must not depend on where the focus is, so the near
 * pass and the band always agree about whose item it is; how *far* the band
 * actually draws it is `FAR_REACH`'s business below.
 *
 * **None of the seven specials is ever a band item**, and that is deliberate
 * rather than an omission. The band exists to carry a handful of things a
 * kilometre; a speciality is a reason to be *standing somewhere*, and a fern
 * bank you could see from a kilometre up would be a biome, not a find. It also
 * keeps the band's per-kind caps and its 49k-super-cell scan exactly as
 * measured, since the set it walks is unchanged.
 */
const farKeeps = (kind: number, h1: number, h2: number): boolean =>
  kind === K_LOG ? true
  : kind === K_STONE ? farSize(kind, h1, h2) >= FAR_STONE
  : kind === K_CLUMP ? farSize(kind, h1, h2) >= FAR_CLUMP
  : false;

/**
 * Metres of reach per metre of item, by kind: a landmark carries to
 * `FAR_REACH × size` and then shrinks away. At 340 that is ~3.3 px of this
 * camera, a little over the ~3 px floor `tools/settlement.ts` set for a
 * signal — the register logs and fists of stone get, so every log (≥3.8 m)
 * crosses the whole band. Clumps get less than half that on purpose: a clump
 * is *tone*, not a landmark — its job is to fringe the dense disc, not to
 * populate the kilometre — and at the full rate a redwood floor was measured
 * carrying 4,900 of them (~10,000 clump instances in all, and ~4 ms of every
 * travelling frame spent recomposing sub-2-px tone). At 190 the fringe runs
 * ~250–420 m by size and the band beyond it belongs to the true fixtures.
 */
const FAR_REACH = [340, 340, 190, 340] as const;

/**
 * Side of the resolve cache, in cells — a *toroidal* grid, indexed by
 * `(ix & mask, iz & mask)`. Because the side comfortably exceeds the widest
 * disc's diameter in cells (a 320 m radius is 321 cells across, this is 512),
 * every live cell lands in its own slot and cells that fall out of the disc are
 * overwritten by the ones that replace them. No eviction pass, no probing, no
 * rehash hitch every few hundred metres of travel — which is what a plain hash
 * map with a load factor would have cost.
 */
const GRID = 512;
const GMASK = GRID - 1;
const SLOTS = GRID * GRID;

/** Disc radius in metres, at the closest view and at the farthest. */
const R_NEAR = 110, R_FAR = 320;
/** Camera orbit radii between which the disc opens from one to the other. */
const CAM_TIGHT = 100, CAM_WIDE = 1000;
/**
 * Beyond this orbit radius the whole layer switches off — and past it litter
 * also stops being pointable, which is why the constant now lives in
 * `world/litter.ts` as `POINTABLE_FAR` and is shared. The landmark band below
 * is what earns the ceiling: an 8 m log is still ~6 px from 1.5 km.
 */
const FAR = POINTABLE_FAR;

/**
 * The landmark band: the few litter items big enough to read from hundreds of
 * metres — fallen logs, fists of stone, the broadest clumps — drawn out to
 * LFAR whatever the dense disc is doing. One item per 8 m super-cell at most
 * (SUPER lattice cells to a side), chosen by a hash of the super-cell's own
 * coordinates, so the band is a pure thinning of the same world lattice: the
 * renderer picking *which* cells to draw is exactly the licence it always had.
 * The near pass skips the cells this band owns, so an item has one owner and
 * there is no seam for it to pop across — a log is simply always there, from
 * ankle range to a kilometre.
 */
const SUPER = 4;
const LFAR = 1000;
/** Metres of rim over which landmark items shrink away at the band's edge. */
const LRIM = 130;
/** Landmark super-cell cache side — 256 super-cells is 2 km, over 2 × LFAR. */
const FGRID = 256;
const FMASK = FGRID - 1;
const FSLOTS = FGRID * FGRID;
/** What the band keeps: drawn size in metres a kind needs to read at range. */
const FAR_STONE = 0.72, FAR_CLUMP = 1.35;

/**
 * The grass carpet: a dense shell of tiny blade clusters underfoot, in the
 * litter clumps' own family but half their size and four times their number.
 * Pure groundcover — it is the ground's texture standing up, not a set of
 * things: it never enters `world/litter.ts`, so nothing in it can be pointed
 * at, named or carried, exactly as the shader's blade stipple cannot. Where
 * the stipple paints the ground at range, this is what it resolves into at
 * ankle height. Density rides the same cache the litter reads — a cell's
 * `growth` share is precisely "how grassy is this ground" — and each cell
 * offers four hash-jittered spots, so the carpet is thick without the
 * lattice ever showing.
 */
const CARPET_R = 85;
const CARPET_RIM = 18;
/**
 * Twelve spots a cell, not four (2026-08-18), and blades of 0.20–0.60 m
 * rather than 0.15–0.40: the close-range look station stands the eye twelve
 * metres up, and from there a 0.15 m blade cluster one to a six-metre square
 * is not grass, it is a fleck. Griffin's direction the same day: a LOT more
 * grass in this layer, because it is the one layer that is cheap — six
 * triangles a cluster, no shadow cast, no pointer, bounded to an 85 m disc
 * however far the litter reaches — where the litter disc's reach is what
 * slows a client down. The tussock field (below) gathers the extra into
 * stands rather than spreading it, so the ground between them stays open;
 * the count is still capped, and the carpet is still not world content.
 * Cost, measured at the close station: 40k clusters is ~1.6 MB of instance
 * matrices per rebuild, and a rebuild is once per 0.75 m of focus travel.
 */
const CARPET_SPOTS = 12;
const CARPET_TARGET = 32000;
const CARPET_CAP = 40000;
/** Eye distance over which the whole carpet sinks away — sub-pixel by then. */
const CARPET_NEAR = 170, CARPET_FAR = 300;
// renderer-only salts: the carpet is not world content, so these live here
const S_CX = 0xc151, S_CZ = 0xc257, S_COCC = 0xc363, S_CSZ = 0xc469, S_CTONE = 0xc571;
/** The tussock field's salt — where grass stands together and where it opens. */
const S_TUSSOCK = 0xc67d;

/**
 * THE FLORA — the carpet's company (2026-08-19, Griffin: *really overdo it on
 * the grass layer, since it is the cheap one*). Four more ground-cover shapes
 * drawn from the same scatter by the same rules — non-pointable, non-casting,
 * bounded to the carpet's disc, never world content — so the meadow stops
 * being one blade cluster repeated forty thousand times:
 *
 *  · **blooms** — a stem with a four-petal head, standing in ONE-COLOUR DRIFTS
 *    (a 5.5 m field picks where, an 11 m cell picks which of the six bloom
 *    colours), because a meadow flowers in drifts and a rainbow scatter reads
 *    as confetti;
 *  · **seed stalks** — bare stalks with a dry ear, taller than the blades,
 *    thickest in the tussocks;
 *  · **clover pads** — flat three-leaflet pads lying on the slope in patches,
 *    the one shape here that is broad rather than upright, which is what
 *    breaks a carpet of vertical marks;
 *  · **fallen leaves** — flat ochre cards where the ground is litter country,
 *    thickest under the wood.
 *
 * Each kind has its own spots a cell and its own cap, so the worst frame is
 * known in advance: at most 39k extra instances of 2–20 triangles, under a
 * third of a million triangles, a fraction of the carpet's own bill. All of
 * it rides `keepC` and the carpet's rim and fade, so wherever the carpet
 * thins or sinks, so does this.
 */
/** Spots a cell, per kind: blooms, seed stalks, clover pads, fallen leaves. */
const FLORA_SPOTS = [10, 5, 8, 6] as const;
const CAP_BLOOM = 14000, CAP_SEED = 9000, CAP_CLOVER = 11000, CAP_LEAF = 10000;
const S_FX = 0xc789, S_FZ = 0xc88f, S_FOCC = 0xca9d, S_FS = 0xcba3, S_FT = 0xccab;
const S_DRIFT = 0xcdb1, S_DRIFT_HUE = 0xceb7, S_PATCH = 0xcfbd, S_LEAFP = 0xd0c3;
/** Fallen-leaf colours: ochre, russet, dun, umber. */
const LEAF_FALL = [
  new THREE.Color(0.50, 0.36, 0.16),
  new THREE.Color(0.44, 0.26, 0.13),
  new THREE.Color(0.52, 0.44, 0.24),
  new THREE.Color(0.36, 0.24, 0.14),
] as const;
const CLOVER = new THREE.Color(0.20, 0.38, 0.15);
/**
 * The carpet's bloom colours — brighter than the litter's BLOOM palette on
 * purpose. A litter flower is a thing with a card, seated in the ground's
 * tone; a carpet bloom is a spot of colour in a field, read at ten to forty
 * metres, and a spot that takes the ground's tone is a spot that vanishes.
 */
const CARPET_BLOOM = [
  new THREE.Color(0.92, 0.90, 0.80),   // white — nearly
  new THREE.Color(0.96, 0.78, 0.20),   // buttercup
  new THREE.Color(0.58, 0.44, 0.84),   // vetch violet
  new THREE.Color(0.88, 0.38, 0.42),   // campion pink
  new THREE.Color(0.46, 0.56, 0.90),   // harebell blue
  new THREE.Color(0.84, 0.58, 0.24),   // ochre
] as const;

/**
 * A lattice cell as one integer, for the drawn-cell sets below. Cells run to
 * about ±13,000 in each axis at STEP 2 over the island; 20,000 clears it.
 */
const cellId = (ix: number, iz: number): number => (ix + 20000) * 40000 + (iz + 20000);

/**
 * The instance budget's CEILING — not the count of what gets drawn. The
 * acceptance threshold is solved for this each rebuild, so a wider disc is a
 * thinner scatter rather than a bigger bill, and richer ground still gets more
 * stuff than poor ground, which is the whole point of asking the biome.
 *
 * The worst case is bounded: the threshold is solved against the biome's litter
 * yield *at the focus*, clamped into [A_REF_LO, A_MAX], so the count can never
 * exceed TARGET × A_MAX / A_REF_LO ≈ 1.21 × TARGET however the disc is
 * composed. HARD_CAP is the last line, and in practice never fires.
 *
 * **Read the word ceiling literally, because `openness` landed after this
 * number was chosen and nothing here was re-derived.** The solve reads
 * `SPEC[biome].amount`, the biome table's figure; `litterCell` then multiplies
 * it by the three patch fields *and* by `openness`, so what the ground really
 * yields is a fraction of what the solve assumed and the drawn count is a
 * fraction of TARGET. `npx tsx tools/litter.ts` prints both halves:
 *
 * ```
 *   realised / assumed occupancy   redwood 0.60   conifer 0.54   oak 0.55
 *                                  grassland 0.33   desert 0.29
 *   disc instances at a 340 m orbit   redwood 2,590   oak 2,311   grassland 1,833
 *   worst of 320 land points at the widest disc (orbit 1000 m)   4,248
 * ```
 *
 * That gap is the intended behaviour and not a bug to close: solving against
 * the realised amount would top a glade back up to TARGET, which is openness
 * undone (see A_REF_LO). What was wrong was this comment, which reasoned in
 * absolute instance counts and sent the next person tuning density to a number
 * that does about half of what it says. **The quantity to tune against is the
 * probe's measured count, and TARGET is the bound over it.**
 */
const TARGET = 5600;
const HARD_CAP = 12000;
/**
 * Per-kind instance capacity. Alpine scree is nearly all stone; forest floor
 * nearly all growth. A cap must never bind before the budget does: the scan
 * runs in rows, so a binding cap truncates a *region* of the disc, a visible
 * line where a kind stops — which is exactly what a redwood floor was
 * measured doing at the first cut of these numbers. Sized to the budget's own
 * ceiling (1.21 × TARGET ≈ 6,800 of one kind in the worst biome) plus the
 * most the landmark band adds over it under its per-kind reach (~1.5k clumps
 * fringing the disc in growth country; a few hundred stones and logs).
 */
const CAP_STONE = 8200, CAP_STICK = 4400, CAP_CLUMP = 8800, CAP_LOG = 360;
/**
 * …and the seven specials, sized against the budget's own ceiling rather than
 * against how rare they *feel*.
 *
 * These are the BOUND, not the measurement, and the two are far apart on
 * purpose. `npx tsx tools/litter.ts` sweeps 320 land points at the widest disc
 * the layer ever builds (orbit 1000 m, R = 320 m) and prints the worst each
 * kind actually reaches, disc and landmark band together:
 *
 * ```
 *   seedhead 91 (marsh)   mushroom 81 (conifer)   fern 71 (oak)
 *   moss 71 (redwood)     flower 58 (oak)         bone 35 (chaparral)
 *   shell 32 (beach)      — and of the ordinary four, clump 3,030, stone 2,675
 * ```
 *
 * Sizing to that would be sizing to a sample. (An earlier cut of this comment
 * quoted 92 seed heads "in open country" and no committed probe printed it; the
 * sweep above is the probe, and it is where these figures now come from.)
 *
 * The bound is different. A speciality replaces its channel's ordinary item, so
 * a disc sitting inside a run of flushes that all happened to elect the same
 * kind could take `PATCH_STRONG` (0.62) of a whole channel, and on the richest
 * ground the growth channel alone is most of the budget's 6,800 ceiling — 4,200.
 * That configuration is vanishingly unlikely (a flush is 8.5% of patches and
 * picks from a pool of two to four), but *a cap must never bind before the
 * budget does*: the scan runs in rows, so a binding cap truncates a **region**
 * of the disc, a visible line where a kind stops — which is exactly what a
 * redwood floor was measured doing at the first cut of the original four
 * numbers. Seven caps at the bound cost about 1.7 MB of instance buffer, which
 * is a cheap price for not owning that failure mode.
 */
const CAP_GROWTH_SPECIAL = 4200, CAP_SHELL = 1500, CAP_BONE = 1500;

/**
 * The least the budget will assume the surrounding ground yields — a floor on
 * `SPEC[biome].amount`, which is the biome TABLE's figure and not the ground's
 * realised one.
 *
 * That distinction was not there when this was written and it matters now.
 * `litterCell` multiplies the table's amount by the patch fields AND by
 * `openness`, which runs 0.10–1.0 (`npx tsx tools/litter.ts` prints p10 0.10,
 * p50 0.43, p90 1.00 over 20,000 land points), so the realised occupancy of a
 * cell can be a tenth of what this clamp assumes: island-wide the realised
 * amount is p10 0.055, p50 0.284, min 0.012, and this floor of 0.76 against
 * openness's own floor of 0.10 corresponds to a realised 0.076. That is deliberate and must stay: the solve
 * reads the table so a glade genuinely draws fewer things than the drift beside
 * it. Feed it the realised amount and the budget would top a glade back up to
 * TARGET, which is openness undone.
 */
const A_REF_LO = 0.76;
/**
 * Width of the occupancy threshold's soft edge. An item whose hash sits within
 * this of the threshold is part-grown, so items admitted by a widening budget
 * swell into place instead of blinking on.
 */
const KEEP_FADE = 0.07;
/** Metres of rim over which items shrink away at the disc's edge. */
const RIM = 26;

/**
 * The budget's own constants, published so `tools/litter.ts` measures the
 * SHIPPED numbers instead of a copy that drifts from them — the mistake
 * `tools/repose.ts` was written to avoid. Nothing in the renderer reads these.
 */
export const DISC_TARGET = TARGET;
export const DISC_A_REF_LO = A_REF_LO;
export const DISC_ORBIT_WIDE = CAM_WIDE;
export const DISC_CAPS: readonly number[] = [
  CAP_STONE, CAP_STICK, CAP_CLUMP, CAP_LOG,
  CAP_SHELL, CAP_BONE, CAP_GROWTH_SPECIAL, CAP_GROWTH_SPECIAL,
  CAP_GROWTH_SPECIAL, CAP_GROWTH_SPECIAL, CAP_GROWTH_SPECIAL,
];

/**
 * How eagerly each kind spends the budget. When the disc widens and the
 * threshold falls, what survives should be what still reads at that range —
 * and that is *not* the twigs. A stick is a bright line one or two pixels wide
 * at two hundred metres, and a field of them reads as confetti scattered over
 * the ground rather than as anything lying on it; a clump is a soft mass that
 * merges with its neighbours into tone. So the clumps now outbid the sticks,
 * and the pebbles — a sub-pixel scattering at that range — still go first.
 * This is chunk.ts's ladder rule applied to litter: coarser views only omit.
 */



/**
 * The palette, measured against the ground it lies on rather than guessed.
 *
 * `chunk.ts` hands the renderer an albedo already pulled down by 0.62, and
 * `tools/palette.ts` says what that comes to: a coast forest floor arrives at
 * about (0.12, 0.19, 0.12) and open grassland at about (0.30, 0.31, 0.21).
 * That is the register these have to live in. The previous set did not — its
 * straw sat at 0.62 and its stone between 0.40 and 0.58, three to five times
 * the forest floor beneath them, and three thousand instances of *that* is not
 * litter on soil, it is confetti thrown over it. The pass before that made the
 * opposite mistake, pitching everything below the ground, and photographed as
 * grime on the lens.
 *
 * So: roughly one and a half to two times the ground, never more. Enough to
 * read as lying on top of it in raking light, not enough to read as a separate
 * bright layer — and at three hundred metres, where a clump is two pixels, the
 * whole scatter settles into tone instead of speckle, which is the only thing
 * sub-metre detail can honestly do at that range.
 */
const WHITE = new THREE.Color(1, 1, 1);
const EARTH = new THREE.Color(0.30, 0.24, 0.17);
const WOOD = new THREE.Color(0.25, 0.20, 0.13);
const STRAW = new THREE.Color(0.44, 0.38, 0.23);
const SEDGE = new THREE.Color(0.17, 0.26, 0.13);
const HAY = new THREE.Color(0.40, 0.36, 0.19);

/**
 * …and the seven specials, in the same register.
 *
 * Every one of these is chosen against the note above rather than against a
 * reference photograph: a coast forest floor arrives at about (0.12, 0.19,
 * 0.12), so anything much past 0.4 is a bright layer thrown over the ground
 * rather than something lying on it. The three that break the rule break it
 * knowingly and by a measured amount:
 *
 *  - **shell and bone** are genuinely pale objects on ground that is not, and
 *    they are the two kinds that only occur where the ground is already pale
 *    (a strand, dry open country). They sit at 3–4× a forest floor and about
 *    1.6× the sand and dust they actually lie on, which is the ratio the rest
 *    of the palette keeps.
 *  - **flowers** are the one thing in the world the eye expects to out-read
 *    its background, which is what a flower is *for*. They are also the only
 *    kind that carries a hue rather than a tone, and the only one whose ground
 *    tint is deliberately weakened (see `TONE_FLOWER`) — a red poppy dimmed to
 *    a coast forest's albedo is a brown lump.
 */
const SHELL_PALE = new THREE.Color(0.62, 0.58, 0.50);
const SHELL_WARM = new THREE.Color(0.58, 0.46, 0.40);
const BONE_PALE = new THREE.Color(0.60, 0.58, 0.51);
const FROND = new THREE.Color(0.13, 0.24, 0.11);
const MOSS_GREEN = new THREE.Color(0.12, 0.22, 0.09);
const CAP_BROWN = new THREE.Color(0.34, 0.24, 0.16);
const CAP_IVORY = new THREE.Color(0.56, 0.52, 0.42);
const SEED_DRY = new THREE.Color(0.42, 0.37, 0.21);

/**
 * The flower hues, and the only place in this layer that carries a hue at all.
 *
 * Six, because a meadow of one colour is a texture and a meadow of a dozen is
 * a fruit bowl. They are muted on purpose — these are the values a *wild*
 * flower has at three metres in overcast light, not the values it has in a
 * photograph of one.
 */
const BLOOM = [
  new THREE.Color(0.52, 0.20, 0.22),   // deep red
  new THREE.Color(0.56, 0.46, 0.16),   // buttercup
  new THREE.Color(0.34, 0.26, 0.48),   // vetch purple
  new THREE.Color(0.60, 0.56, 0.50),   // white, which is never white
  new THREE.Color(0.56, 0.34, 0.18),   // ochre
  new THREE.Color(0.24, 0.30, 0.46),   // harebell blue
] as const;
/** How much of the ground's own tone a flower takes. See the palette note. */
const TONE_FLOWER = 0.35;

/**
 * …and then scaled by the ground it is actually lying on.
 *
 * One palette cannot serve every floor on the island. `BIOMES` spans a factor
 * of two and a half from a coast forest's soil to a beach's sand, so a single
 * set of litter tones sits at about 1.5× the ground in open country and 3× in
 * a closed canopy — and 3× is the difference between litter and confetti. It is
 * also the shape of a bug that keeps coming back: every time the ground palette
 * moves, this layer has to be re-tuned by hand, and between those tunings it is
 * wrong.
 *
 * So it is not tuned by hand. The scale is read from the biome's own table
 * entry, which means a future change to `BIOMES` carries this layer with it and
 * the ratio of litter to floor stays put wherever the ground goes.
 */
const TONE_LO = 40, TONE_HI = 120;          // the darkest and palest soils in BIOMES
const TONE_MIN = 0.62, TONE_MAX = 1.15;     // …and what they are worth as litter

const biomeTone = (b: Biome | undefined): number => {
  if (!b) return 1;
  const [lo, hi] = b.colour;
  const mean = (lo[0] + lo[1] + lo[2] + hi[0] + hi[1] + hi[2]) / 6;
  return lerp(TONE_MIN, TONE_MAX, clamp((mean - TONE_LO) / (TONE_HI - TONE_LO), 0, 1));
};

// ---------------------------------------------------------------------------
// The four shapes
// ---------------------------------------------------------------------------

const ATTRS = [['position', 3], ['normal', 3], ['color', 3], ['flex', 1]] as const;

/** Triangle soup: everything merges non-indexed, like vegetation and structures. */
function solid(g: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!g.index) return g;
  const flat = g.toNonIndexed();
  g.dispose();
  return flat;
}

/**
 * Finish one part: a grey vertical ramp in the colour channel (so the instance
 * colour carries the hue and the underside still reads as shadowed), and the
 * part's share of the wind sway in `flex`, growing as the square of height so
 * roots are rigid and tips are loose. Call after every transform — it reads
 * final positions.
 */
function dress(
  g: THREE.BufferGeometry, yLow: number, yHigh: number, shadeLow: number, flexAmp: number,
  /**
   * A per-part multiplier on the shade, so one merged shape can carry two
   * materials without a second draw call: a mushroom's stem is paler than its
   * cap, a flower's head is the whole hue and its stem is not. The instance
   * colour still carries the hue, so this may only ever say *relatively*
   * paler or darker — an absolute colour here would fight the palette.
   */
  tint: readonly [number, number, number] = [1, 1, 1],
): THREE.BufferGeometry {
  const p = g.attributes.position.array as ArrayLike<number>;
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  const fl = new Float32Array(n);
  const span = Math.max(1e-6, yHigh - yLow);
  for (let i = 0; i < n; i++) {
    const t = clamp((p[i * 3 + 1] - yLow) / span, 0, 1);
    const s = lerp(shadeLow, 1, t);
    col[i * 3] = s * tint[0]; col[i * 3 + 1] = s * tint[1]; col[i * 3 + 2] = s * tint[2];
    fl[i] = flexAmp * t * t;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('flex', new THREE.BufferAttribute(fl, 1));
  if (g.attributes.uv) g.deleteAttribute('uv');
  return g;
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  for (const [name, size] of ATTRS) {
    const arrs = parts.map((p) => p.attributes[name].array as Float32Array);
    const all = new Float32Array(arrs.reduce((s, a) => s + a.length, 0));
    let o = 0;
    for (const a of arrs) { all.set(a, o); o += a.length; }
    geo.setAttribute(name, new THREE.BufferAttribute(all, size));
  }
  parts.forEach((p) => p.dispose());
  geo.computeBoundingSphere();
  return geo;
}

/**
 * A pebble: an icosahedron whose vertices are shoved about by a hash of their
 * own rounded position — duplicates in the triangle soup hash alike, so the
 * facets meeting at a corner move together and the shell never cracks — then
 * squashed and normalised to stand on y ∈ [0, 1], x, z ∈ [−0.5, 0.5]. One
 * shape; the per-instance axis scales make it a flat chip or a round lump.
 * 20 triangles.
 */
function stoneGeometry(): THREE.BufferGeometry {
  const g = solid(new THREE.IcosahedronGeometry(0.5, 0));
  const a = g.attributes.position.array as Float32Array;
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < g.attributes.position.count; i++) {
    const o = i * 3;
    const x = a[o], y = a[o + 1], z = a[o + 2];
    const kx = Math.round(x * 4096), ky = Math.round(y * 4096), kz = Math.round(z * 4096);
    a[o] = x + (hash01(kx, ky * 7919 + kz, 0x3311) - 0.5) * 0.30;
    a[o + 1] = (y + (hash01(ky, kz * 7919 + kx, 0x3312) - 0.5) * 0.26) * 0.8;
    a[o + 2] = z + (hash01(kz, kx * 7919 + ky, 0x3313) - 0.5) * 0.30;
    lo = Math.min(lo, a[o + 1]); hi = Math.max(hi, a[o + 1]);
  }
  const k = 1 / Math.max(1e-6, hi - lo);
  for (let i = 0; i < g.attributes.position.count; i++) a[i * 3 + 1] = (a[i * 3 + 1] - lo) * k;
  g.computeVertexNormals();
  return merge([dress(g, 0, 1, 0.52, 0)]);
}

/**
 * A twig, lying along +x: a five-sided tapered prism, one unit long, one unit
 * in radius, lifted so its underside is the y = 0 plane. Per-instance scale is
 * (length, radius, radius), so the instance's origin is the patch of ground it
 * rests on whatever its thickness. 10 triangles.
 */
function stickGeometry(): THREE.BufferGeometry {
  const g = solid(new THREE.CylinderGeometry(0.52, 1, 1, 5, 1, true))
    .rotateZ(-Math.PI / 2)
    .translate(0, 1, 0);
  return merge([dress(g, 0, 2, 0.6, 0)]);
}

/**
 * A small clump of blades — sedge, bunchgrass, the green that grows in the gaps
 * chunk vegetation's 0.4 m tuft is too big to fill. Each blade is a triangle
 * and its mirror, so the clump reads from behind without a two-sided material.
 * Unit-scaled about its base like every plant in `vegetation.ts`: y ∈ [0, 1],
 * x, z ∈ [−0.5, 0.5]. 10 triangles.
 */
function clumpGeometry(): THREE.BufferGeometry {
  const count = 7;
  const pos = new Float32Array(count * 18);
  const nor = new Float32Array(count * 18);
  const WIND = [0, 1, 2, 1, 0, 2];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * TAU + hash01(i, 0, 0x4401) * 0.9;
    const root = 0.03 + hash01(i, 1, 0x4402) * 0.09;
    const h = 0.5 + hash01(i, 2, 0x4403) * 0.5;
    const lean = 0.18 + hash01(i, 3, 0x4404) * 0.28;
    const w = 0.03 + hash01(i, 4, 0x4405) * 0.03;
    const ca = Math.cos(a), sa = Math.sin(a);
    const tip = root + lean * h;
    const v: [number, number, number][] = [
      [ca * root + sa * w, 0, sa * root - ca * w],
      [ca * root - sa * w, 0, sa * root + ca * w],
      [ca * tip, h, sa * tip],
    ];
    const ex = v[1][0] - v[0][0], ey = v[1][1] - v[0][1], ez = v[1][2] - v[0][2];
    const fx = v[2][0] - v[0][0], fy = v[2][1] - v[0][1], fz = v[2][2] - v[0][2];
    let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    for (let f = 0; f < 6; f++) {
      const o = i * 18 + f * 3;
      const side = f < 3 ? 1 : -1;
      const p = v[WIND[f]];
      pos[o] = p[0]; pos[o + 1] = p[1]; pos[o + 2] = p[2];
      nor[o] = nx * side; nor[o + 1] = ny * side; nor[o + 2] = nz * side;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  return merge([dress(g, 0, 1, 0.5, 0.17)]);
}

/**
 * A carpet blade-cluster: three thin blades, the clump's construction at a
 * third of its triangles. Unit-scaled about its base like everything else;
 * per-instance scale makes it ankle grass. 6 triangles, and the one shape
 * here that exists to be drawn ten thousand times.
 */
function carpetGeometry(): THREE.BufferGeometry {
  const count = 3;
  const pos = new Float32Array(count * 18);
  const nor = new Float32Array(count * 18);
  const WIND = [0, 1, 2, 1, 0, 2];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * TAU + hash01(i, 7, 0xc001) * 1.4;
    const root = 0.02 + hash01(i, 8, 0xc002) * 0.07;
    const h = 0.62 + hash01(i, 9, 0xc003) * 0.38;
    const lean = 0.16 + hash01(i, 10, 0xc004) * 0.30;
    const w = 0.030 + hash01(i, 11, 0xc005) * 0.025;
    const ca = Math.cos(a), sa = Math.sin(a);
    const tip = root + lean * h;
    const v: [number, number, number][] = [
      [ca * root + sa * w, 0, sa * root - ca * w],
      [ca * root - sa * w, 0, sa * root + ca * w],
      [ca * tip, h, sa * tip],
    ];
    const ex = v[1][0] - v[0][0], ey = v[1][1] - v[0][1], ez = v[1][2] - v[0][2];
    const fx = v[2][0] - v[0][0], fy = v[2][1] - v[0][1], fz = v[2][2] - v[0][2];
    let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    for (let f = 0; f < 6; f++) {
      const o = i * 18 + f * 3;
      const side = f < 3 ? 1 : -1;
      const p = v[WIND[f]];
      pos[o] = p[0]; pos[o + 1] = p[1]; pos[o + 2] = p[2];
      nor[o] = nx * side; nor[o + 1] = ny * side; nor[o + 2] = nz * side;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  return merge([dress(g, 0, 1, 0.42, 0.30)]);
}

/**
 * A fallen log: a capped six-sided taper along +x with one snapped stub of
 * branch, underside on y = 0. The one thing in this layer that is *not*
 * sub-metre, and it earns it — a 4 m trunk lying across a slope is the single
 * clearest scale reference the near ground has, and it says the forest here has
 * been standing long enough to lose a tree. 32 triangles, a few dozen ever
 * drawn at once.
 */
function logGeometry(): THREE.BufferGeometry {
  const trunk = solid(new THREE.CylinderGeometry(0.42, 0.5, 1, 6, 1, false))
    .rotateZ(-Math.PI / 2)
    .translate(0, 0.5, 0);
  const stub = solid(new THREE.CylinderGeometry(0.1, 0.16, 0.5, 4, 1, true))
    .rotateZ(-0.9)
    .translate(0.16, 0.62, 0.1);
  return merge([dress(trunk, 0, 1, 0.46, 0), dress(stub, 0, 1, 0.55, 0)]);
}

// ---------------------------------------------------------------------------
// The seven specialities
//
// Same conventions as the four above: the things that STAND are authored into
// y ∈ [0, 1], x and z in [−0.5, 0.5] and scaled per instance as (width, height,
// width); the things that LIE along the ground are authored one unit long on
// +x, one unit in radius, underside on y = 0, and scaled as (length, radius,
// radius) — which is the stick's convention and is what lets `drawItem` lay a
// bone down the slope with exactly the stick's arithmetic.
// ---------------------------------------------------------------------------

/**
 * A double-sided ribbon through a list of nodes, appended into `pos`/`nor`.
 *
 * The one construction the three leafy specialities share. Every plant shape in
 * this layer is triangles with no back-face material, so every quad has to be
 * emitted twice with opposed normals — writing that out three times is how the
 * fern ends up shaded differently from the seed head for no reason anybody can
 * find later.
 */
function ribbon(
  nodes: ReadonlyArray<{ x: number; y: number; z: number; w: number }>,
  sx: number, sz: number,
  pos: number[], nor: number[],
): void {
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1];
    const v = [
      [a.x - sx * a.w, a.y, a.z - sz * a.w],
      [a.x + sx * a.w, a.y, a.z + sz * a.w],
      [b.x + sx * b.w, b.y, b.z + sz * b.w],
      [b.x - sx * b.w, b.y, b.z - sz * b.w],
    ];
    const ex = v[1][0] - v[0][0], ey = v[1][1] - v[0][1], ez = v[1][2] - v[0][2];
    const fx = v[3][0] - v[0][0], fy = v[3][1] - v[0][1], fz = v[3][2] - v[0][2];
    let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    const tri = [[0, 1, 2], [0, 2, 3]];
    for (const t of tri) {
      for (const side of [1, -1]) {
        const order = side > 0 ? t : [t[0], t[2], t[1]];
        for (const k of order) {
          pos.push(v[k][0], v[k][1], v[k][2]);
          nor.push(nx * side, ny * side, nz * side);
        }
      }
    }
  }
}

/** Turn the two loose arrays `ribbon` fills into a geometry. */
function fromRibbons(pos: number[], nor: number[]): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(nor), 3));
  return g;
}

/**
 * A shell: a fluted dome, wider than it is tall, lying open side down.
 *
 * The flutes are what make it a shell rather than a bead — seven ribs cut into
 * the radius by a cosine of the vertex's own bearing, so duplicates in the
 * triangle soup move together and the surface stays welded, the same rule the
 * pebble's jitter follows.
 */
function shellGeometry(): THREE.BufferGeometry {
  const g = solid(new THREE.SphereGeometry(0.5, 7, 3, 0, TAU, 0, Math.PI / 2));
  const a = g.attributes.position.array as Float32Array;
  let hi = 1e-6;
  for (let i = 0; i < g.attributes.position.count; i++) {
    const o = i * 3;
    const rib = 0.86 + 0.14 * Math.cos(Math.atan2(a[o + 2], a[o]) * 7);
    a[o] *= rib; a[o + 2] *= rib;
    hi = Math.max(hi, a[o + 1]);
  }
  // normalise the dome onto y ∈ [0, 1] so the per-instance height means height
  for (let i = 0; i < g.attributes.position.count; i++) a[i * 3 + 1] /= hi;
  g.scale(1, 1, 0.84);
  g.computeVertexNormals();
  return merge([dress(g, 0, 1, 0.58, 0)]);
}

/** A bone: a shaft with a knuckle at each end, lying along +x like a stick. */
function boneGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const shaft = solid(new THREE.CylinderGeometry(0.60, 0.60, 1, 5, 1, true))
    .rotateZ(-Math.PI / 2)
    .translate(0, 1, 0);
  parts.push(dress(shaft, 0, 2, 0.60, 0));
  for (const e of [-0.5, 0.5]) {
    const k = solid(new THREE.IcosahedronGeometry(1.05, 0));
    k.scale(0.5, 1, 1);
    k.translate(e, 1, 0);
    parts.push(dress(k, 0, 2, 0.58, 0));
  }
  return merge(parts);
}

/**
 * A fern: five arching fronds, each a three-node ribbon that widens and then
 * runs out.
 *
 * The arch is the whole shape. A frond drawn as one straight triangle is a
 * blade of grass at fern scale, and a bank of those is a bank of grass — the
 * thing the layer already had too much of.
 */
function fernGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [];
  const FRONDS = 5;
  for (let i = 0; i < FRONDS; i++) {
    const a = (i / FRONDS) * TAU + hash01(i, 0, 0x5501) * 1.0;
    const ca = Math.cos(a), sa = Math.sin(a);
    const h = 0.72 + hash01(i, 1, 0x5502) * 0.28;
    const out = 0.34 + hash01(i, 2, 0x5503) * 0.18;
    const w = 0.055 + hash01(i, 3, 0x5504) * 0.030;
    ribbon([
      { x: ca * 0.04, y: 0, z: sa * 0.04, w: w * 0.5 },
      { x: ca * out * 0.45, y: h * 0.62, z: sa * out * 0.45, w },
      { x: ca * out, y: h * 0.95, z: sa * out, w: w * 0.75 },
      { x: ca * out * 1.30, y: h * 0.84, z: sa * out * 1.30, w: w * 0.12 },
    ], -sa, ca, pos, nor);
  }
  return merge([dress(fromRibbons(pos, nor), 0, 1, 0.44, 0.13)]);
}

/**
 * A flower: a low pair of leaves, four short stems, and four heads that are
 * most of what you see.
 *
 * Weighted toward the head on purpose. The instance colour carries one hue for
 * the whole shape, so a tall-stemmed flower would be a plant with red stalks;
 * making the head the bulk of the silhouette means the hue lands where a hue
 * belongs and the little that is left over reads as shadow.
 */
function flowerGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  {
    const pos: number[] = [], nor: number[] = [];
    for (let i = 0; i < 2; i++) {
      const a = i * 2.3 + 0.4;
      const ca = Math.cos(a), sa = Math.sin(a);
      ribbon([
        { x: 0, y: 0.02, z: 0, w: 0.03 },
        { x: ca * 0.34, y: 0.10, z: sa * 0.34, w: 0.06 },
        { x: ca * 0.50, y: 0.07, z: sa * 0.50, w: 0.01 },
      ], -sa, ca, pos, nor);
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + 0.9;
      const ca = Math.cos(a), sa = Math.sin(a);
      const lean = 0.10 + hash01(i, 4, 0x5601) * 0.13;
      const h = 0.62 + hash01(i, 5, 0x5602) * 0.24;
      ribbon([
        { x: 0, y: 0, z: 0, w: 0.016 },
        { x: ca * lean, y: h, z: sa * lean, w: 0.012 },
      ], -sa, ca, pos, nor);
    }
    // leaves and stems: green, and darker than the bloom by a long way
    parts.push(dress(fromRibbons(pos, nor), 0, 1, 0.5, 0.16, [0.30, 0.46, 0.22]));
  }
  {
    const pos: number[] = [], nor: number[] = [];
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + 0.9;
      const ca = Math.cos(a), sa = Math.sin(a);
      const lean = 0.10 + hash01(i, 4, 0x5601) * 0.13;
      const h = 0.62 + hash01(i, 5, 0x5602) * 0.24;
      const r = 0.10 + hash01(i, 6, 0x5603) * 0.05;
      // a flat three-petal head, lying across the top of its stem
      for (let p = 0; p < 3; p++) {
        const b = a + (p / 3) * TAU;
        const cb = Math.cos(b), sb = Math.sin(b);
        ribbon([
          { x: ca * lean, y: h, z: sa * lean, w: 0.035 },
          { x: ca * lean + cb * r, y: h + 0.03, z: sa * lean + sb * r, w: 0.045 },
          { x: ca * lean + cb * r * 1.5, y: h + 0.02, z: sa * lean + sb * r * 1.5, w: 0.012 },
        ], -sb, cb, pos, nor);
      }
    }
    parts.push(dress(fromRibbons(pos, nor), 0.5, 1, 0.86, 0.16));
  }
  return merge(parts);
}

/** A mushroom: a pale stem and a dome cap, and nothing else at this size. */
function mushroomGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const stem = solid(new THREE.CylinderGeometry(0.09, 0.14, 0.62, 5, 1, true))
    .translate(0, 0.31, 0);
  parts.push(dress(stem, 0, 0.62, 0.62, 0, [1.42, 1.36, 1.20]));
  const cap = solid(new THREE.SphereGeometry(0.5, 8, 3, 0, TAU, 0, Math.PI / 2));
  cap.scale(1, 0.84, 1);
  cap.translate(0, 0.58, 0);
  cap.computeVertexNormals();
  parts.push(dress(cap, 0.58, 1, 0.66, 0));
  return merge(parts);
}

/**
 * Moss: a low cushion, and deliberately the flattest thing in the layer.
 *
 * It is the one special that is a *surface* rather than an object, so it earns
 * its read from colour and profile instead of silhouette — a saturated green
 * that sits below the ground's own value, at a fifth of its own width in
 * height. Anything taller reads as a stone with algae on it.
 */
function mossGeometry(): THREE.BufferGeometry {
  const g = solid(new THREE.IcosahedronGeometry(0.5, 0));
  const a = g.attributes.position.array as Float32Array;
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < g.attributes.position.count; i++) {
    const o = i * 3;
    const x = a[o], y = a[o + 1], z = a[o + 2];
    const kx = Math.round(x * 4096), ky = Math.round(y * 4096), kz = Math.round(z * 4096);
    a[o] = x + (hash01(kx, ky * 7919 + kz, 0x5711) - 0.5) * 0.34;
    a[o + 1] = y + (hash01(ky, kz * 7919 + kx, 0x5712) - 0.5) * 0.20;
    a[o + 2] = z + (hash01(kz, kx * 7919 + ky, 0x5713) - 0.5) * 0.34;
    lo = Math.min(lo, a[o + 1]); hi = Math.max(hi, a[o + 1]);
  }
  const k = 1 / Math.max(1e-6, hi - lo);
  for (let i = 0; i < g.attributes.position.count; i++) a[i * 3 + 1] = (a[i * 3 + 1] - lo) * k;
  g.computeVertexNormals();
  return merge([dress(g, 0, 1, 0.46, 0)]);
}

/** Seed heads: tall bare stalks, each with a small dry ear at the top. */
function seedheadGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [];
  const N = 5;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * TAU + hash01(i, 0, 0x5801) * 1.2;
    const ca = Math.cos(a), sa = Math.sin(a);
    const h = 0.74 + hash01(i, 1, 0x5802) * 0.26;
    const lean = 0.06 + hash01(i, 2, 0x5803) * 0.16;
    ribbon([
      { x: ca * 0.03, y: 0, z: sa * 0.03, w: 0.012 },
      { x: ca * lean, y: h * 0.68, z: sa * lean, w: 0.010 },
    ], -sa, ca, pos, nor);
    // the ear: a narrow spindle, which is the only part that reads at range
    ribbon([
      { x: ca * lean, y: h * 0.68, z: sa * lean, w: 0.012 },
      { x: ca * lean * 1.2, y: h * 0.86, z: sa * lean * 1.2, w: 0.045 },
      { x: ca * lean * 1.4, y: h, z: sa * lean * 1.4, w: 0.008 },
    ], -sa, ca, pos, nor);
  }
  return merge([dress(fromRibbons(pos, nor), 0, 1, 0.48, 0.24)]);
}

/**
 * A bloom for the carpet: one stem and a four-petal head lying across its
 * top. 20 triangles. The head is the whole point — at this size a bloom is a
 * spot of colour standing off the green — and the instance colour carries
 * the hue; the stem's tint keeps it a shade of green against any of them.
 */
function bloomGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  {
    const pos: number[] = [], nor: number[] = [];
    ribbon([{ x: 0, y: 0, z: 0, w: 0.02 }, { x: 0.06, y: 1.0, z: 0.02, w: 0.013 }], 0.96, 0.28, pos, nor);
    parts.push(dress(fromRibbons(pos, nor), 0, 1, 0.5, 0.22, [0.30, 0.46, 0.22]));
  }
  {
    const pos: number[] = [], nor: number[] = [];
    for (let p = 0; p < 4; p++) {
      const b = (p / 4) * TAU + 0.4;
      const cb = Math.cos(b), sb = Math.sin(b);
      // a shallow cup, not a flat disc: petals rise as they go out, so the
      // head shows from the low oblique angle the eye actually sees a meadow
      // at — a flat head at 7 m up and 20 m off is two pixels tall
      ribbon([
        { x: 0.06, y: 0.98, z: 0.02, w: 0.08 },
        { x: 0.06 + cb * 0.30, y: 1.16, z: 0.02 + sb * 0.30, w: 0.045 },
      ], -sb, cb, pos, nor);
    }
    parts.push(dress(fromRibbons(pos, nor), 0.9, 1.16, 0.88, 0.22));
  }
  return merge(parts);
}

/**
 * A seed stalk for the carpet: one bare stalk with a dry ear, the single
 * most legible upright mark a meadow has at ankle height. 12 triangles.
 */
function seedStalkGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [];
  ribbon([
    { x: 0, y: 0, z: 0, w: 0.014 },
    { x: 0.08, y: 0.70, z: 0.03, w: 0.011 },
  ], 0.35, 0.94, pos, nor);
  ribbon([
    { x: 0.08, y: 0.70, z: 0.03, w: 0.014 },
    { x: 0.11, y: 0.87, z: 0.04, w: 0.05 },
    { x: 0.14, y: 1.0, z: 0.05, w: 0.01 },
  ], 0.35, 0.94, pos, nor);
  return merge([dress(fromRibbons(pos, nor), 0, 1, 0.48, 0.26)]);
}

/**
 * A clover pad: three leaflets lying flat, top faces only — the underside
 * is the ground. 6 triangles, and the one broad shape in the carpet.
 */
function cloverGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [];
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * TAU + 0.3;
    const ca = Math.cos(a), sa = Math.sin(a);
    const v = [
      [0, 0.02, 0],
      [ca * 0.45 - sa * 0.30, 0.06, sa * 0.45 + ca * 0.30],
      [ca * 0.80, 0.08, sa * 0.80],
      [ca * 0.45 + sa * 0.30, 0.06, sa * 0.45 - ca * 0.30],
    ];
    for (const t of [[0, 2, 1], [0, 3, 2]]) {
      const A = v[t[0]], B = v[t[1]], C = v[t[2]];
      const ex = B[0] - A[0], ey = B[1] - A[1], ez = B[2] - A[2];
      const fx = C[0] - A[0], fy = C[1] - A[1], fz = C[2] - A[2];
      let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
      if (ny < 0) { nx = -nx; ny = -ny; nz = -nz; }
      const len = Math.hypot(nx, ny, nz) || 1;
      for (const k of (ny >= 0 ? [0, 1, 2] : [0, 2, 1])) {
        const P = [A, B, C][k];
        pos.push(P[0], P[1], P[2]); nor.push(nx / len, ny / len, nz / len);
      }
    }
  }
  const g = fromRibbons(pos, nor);
  return merge([dress(g, 0, 0.08, 0.8, 0)]);
}

/**
 * A fallen leaf: one card folded once along its spine, top face only. 2
 * triangles — the cheapest thing in the layer, and it has to be, because a
 * forest floor wants thousands.
 */
function leafGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [];
  const v = [[-0.5, 0.0, 0], [0, 0.06, -0.30], [0.5, 0.0, 0], [0, 0.06, 0.30]];
  for (const t of [[0, 1, 2], [0, 2, 3]]) {
    const A = v[t[0]], B = v[t[1]], C = v[t[2]];
    const ex = B[0] - A[0], ey = B[1] - A[1], ez = B[2] - A[2];
    const fx = C[0] - A[0], fy = C[1] - A[1], fz = C[2] - A[2];
    let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
    const order = ny >= 0 ? [A, B, C] : [A, C, B];
    if (ny < 0) { nx = -nx; ny = -ny; nz = -nz; }
    const len = Math.hypot(nx, ny, nz) || 1;
    for (const P of order) { pos.push(P[0], P[1], P[2]); nor.push(nx / len, ny / len, nz / len); }
  }
  return merge([dress(fromRibbons(pos, nor), 0, 0.06, 0.9, 0)]);
}

// ---------------------------------------------------------------------------

/**
 * ONE OF EACH, AS THE WORLD DEALS IT — the size and colour a shape is drawn
 * at, for a caller that has no ground to deal it on (`src/workshop/`).
 *
 * `q` is one number standing in for the placement's own `h1`/`h2`/`h4` hashes:
 * 0 is the smallest and palest this shape is ever dealt, 1 the largest and
 * deepest. The governing dimension comes from `world/litter.ts`'s own
 * `litterSize` — imported, never restated — and the proportions and colour
 * mixes below MIRROR the placement switch further down this file. That is a
 * duplication and it is a deliberate one: the placement needs a facet, a
 * slope, a biome tone and a cure level to run at all, none of which exist in a
 * white room. **If you change a proportion or a mix down there, change it
 * here too** — the whole value of the workshop is that a specimen on its shelf
 * is the thing the ground grows.
 *
 * The five flora shapes are not in `litterSize` (they are the carpet's
 * company, sized where they are placed), so their ranges are the lerps at
 * their own placement.
 */
export interface LitterSpecimen {
  /** (x, y, z) metres. */
  scale: readonly [number, number, number];
  colour: THREE.Color;
  /** Whether this shape casts a shadow in the game. See `createNearDetail`. */
  casts: boolean;
}

export function litterSpecimen(shape: LitterShape, q: number): LitterSpecimen {
  const c = new THREE.Color();
  const mix = (a: THREE.Color, b: THREE.Color, t: number) => c.copy(a).lerp(b, t);
  switch (shape) {
    case 'stone': {
      const w = litterSize(K_STONE, q, q), h = w * lerp(0.42, 0.95, q);
      const g = lerp(0.24, 0.41, q);
      c.setRGB(g * 1.02, g, g * 1.10).lerp(EARTH, (1 - q) * 0.3);
      return { scale: [w * lerp(0.8, 1.2, q), h, w * lerp(0.8, 1.2, q)], colour: c, casts: true };
    }
    case 'stick': {
      const len = litterSize(K_STICK, q, q), rad = lerp(0.021, 0.048, q) * MODEL;
      return { scale: [len, rad, rad], colour: mix(WOOD, STRAW, q * 0.7), casts: false };
    }
    case 'clump': {
      const w = litterSize(K_CLUMP, q, q);
      return { scale: [w, w / lerp(1.15, 2.1, q), w], colour: mix(SEDGE, HAY, q * 0.5), casts: true };
    }
    case 'log': {
      const len = litterSize(K_LOG, q, q), dia = lerp(0.16, 0.36, q) * MODEL;
      return { scale: [len, dia, dia], colour: mix(WOOD, STRAW, q * 0.45), casts: true };
    }
    case 'shell': {
      const w = litterSize(K_SHELL, q, q);
      return { scale: [w, w * lerp(0.30, 0.50, q), w], colour: mix(SHELL_PALE, SHELL_WARM, q * 0.8), casts: false };
    }
    case 'bone': {
      const len = litterSize(K_BONE, q, q), rad = len * lerp(0.045, 0.075, q);
      return { scale: [len, rad, rad], colour: mix(BONE_PALE, EARTH, (1 - q) * 0.34), casts: false };
    }
    case 'fern': {
      const w = litterSize(K_FERN, q, q);
      return { scale: [w, w * lerp(0.62, 0.90, q), w], colour: mix(FROND, SEDGE, q * 0.45), casts: true };
    }
    case 'flower': {
      const w = litterSize(K_FLOWER, q, q);
      c.copy(BLOOM[Math.min(BLOOM.length - 1, Math.floor(q * BLOOM.length))]);
      return { scale: [w, w * lerp(1.20, 1.80, q), w], colour: c, casts: false };
    }
    case 'mushroom': {
      const w = litterSize(K_MUSHROOM, q, q);
      return { scale: [w, w * lerp(1.00, 1.50, q), w], colour: mix(CAP_BROWN, CAP_IVORY, q), casts: true };
    }
    case 'moss': {
      const w = litterSize(K_MOSS, q, q);
      return { scale: [w, w * lerp(0.16, 0.28, q), w], colour: mix(MOSS_GREEN, SEDGE, q * 0.40), casts: false };
    }
    case 'seedhead': {
      const w = litterSize(K_SEEDHEAD, q, q);
      return { scale: [w, w * lerp(1.60, 2.30, q), w], colour: mix(SEED_DRY, STRAW, q * 0.7), casts: false };
    }
    // ---- the carpet's company: sized where they are placed, not in litterSize
    case 'carpet': {
      const h = lerp(0.20, 0.60, q);
      c.copy(SEDGE).lerp(HAY, q * 0.6);
      return { scale: [h * lerp(1.1, 1.9, q), h, h * lerp(1.1, 1.9, q)], colour: c, casts: false };
    }
    case 'bloom': {
      const h = lerp(0.22, 0.44, q);
      c.copy(CARPET_BLOOM[Math.floor(q * CARPET_BLOOM.length) % CARPET_BLOOM.length])
        .multiplyScalar(0.9 + q * 0.2);
      return { scale: [h, h, h], colour: c, casts: false };
    }
    case 'seedstalk': {
      const h = lerp(0.34, 0.72, q);
      return { scale: [h * 0.8, h, h * 0.8], colour: mix(HAY, SEED_DRY, 0.4 + q * 0.5), casts: false };
    }
    case 'clover': {
      const w = lerp(0.16, 0.32, q);
      return { scale: [w, w * 0.35, w], colour: mix(CLOVER, HAY, q * 0.4), casts: false };
    }
    default: {
      const w = lerp(0.08, 0.17, q);
      c.copy(LEAF_FALL[Math.floor(q * LEAF_FALL.length) % LEAF_FALL.length])
        .multiplyScalar(0.85 + q * 0.4);
      return { scale: [w, w * 0.5, w], colour: c, casts: false };
    }
  }
}

/** The named litter shapes — see `litterKit`. */
export type LitterShape =
  | 'stone'
  | 'stick'
  | 'clump'
  | 'log'
  | 'shell'
  | 'bone'
  | 'fern'
  | 'flower'
  | 'mushroom'
  | 'moss'
  | 'seedhead'
  | 'carpet'
  | 'bloom'
  | 'seedstalk'
  | 'clover'
  | 'leaf';

export interface LitterKit {
  material: THREE.MeshStandardMaterial;
  /** The sway clock. The owner advances it; see `NearDetail.update`. */
  wind: { value: number };
  shapes: Record<LitterShape, THREE.BufferGeometry>;
}

/**
 * THE GROUND LAYER'S SHAPES AND ITS MATERIAL, separated from the travelling
 * disc that places them.
 *
 * `createNearDetail` is the game's only caller and it wants all of this seated
 * on a lattice under a moving focus. `src/workshop/` is the other caller and
 * wants one of each, on a shelf, standing still. Split out rather than
 * duplicated for the reason `render/rocks.ts` gives at `rockShapes`: a lab
 * drawing its own copy of a shape stops being evidence about the game the
 * first time one of the two is edited.
 *
 * Each call returns a fresh material and fresh geometries — they are cheap,
 * and two scenes sharing a material also share its wind clock, which is not
 * what either of them wants.
 */
export function litterKit(): LitterKit {
  /**
   * One material for all four shapes: vertex colours carry the vertical shade
   * ramp, the instance colour carries the hue, and a wind patch leans anything
   * with a non-zero `flex` — which is the clumps and nothing else. Same idea as
   * the vegetation kit's sway, phased by world position so neighbours move
   * together and no two patches of ground breathe in step.
   */
  const wind = { value: 0 };
  const material = new THREE.MeshStandardMaterial({
    roughness: 0.94, metalness: 0, vertexColors: true, flatShading: true,
  });
  material.onBeforeCompile = (sh) => {
    sh.uniforms.uTime = wind;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime;\nattribute float flex;\nvarying float vTip;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        {
          vec4 iw = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          float ph = iw.x * 0.31 + iw.z * 0.22;
          float g = sin(uTime * 1.6 + ph) * 0.5 + sin(uTime * 2.7 + ph * 1.7) * 0.24;
          transformed.xz += g * flex * vec2(0.94, 0.34);
          // how far up a blade this vertex is: flex is 0 at every root and at
          // every rigid thing, and rises to 0.17–0.30 at a blade's tip
          vTip = clamp(flex * 4.0, 0.0, 1.0);
        }`);
    // BLADE TRANSMISSION (2026-08-18) — the same idea as the vegetation kit's
    // foliage term, for grass: a blade between the eye and the sun is lit
    // through, most at its tip and not at all at its root, so a low sun finds
    // the tips of a meadow and leaves the bases dark. Gated by the fragment's
    // own shadow, which the map cannot resolve at blade scale anyway.
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vTip;')
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
      #if NUM_DIR_LIGHTS > 0
      if (vTip > 0.001) {
        float lit = 1.0;
        #if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
        lit = getShadow( directionalShadowMap[ 0 ], directionalLightShadows[ 0 ].shadowMapSize,
                         directionalLightShadows[ 0 ].shadowBias, directionalLightShadows[ 0 ].shadowRadius,
                         vDirectionalShadowCoord[ 0 ] );
        #endif
        vec3 L = directionalLights[ 0 ].direction;
        float ndl = dot( geometryNormal, L );
        // a blade's tip transmits about as much as its face reflects — grass
        // against a low sun is BRIGHTER than the lit ground behind it, not
        // darker, which is the one thing every reference frame of a meadow at
        // evening agrees on; the tint leans to the yellow a leaf lets through
        float through = clamp( -ndl, 0.0, 1.0 ) * 1.1;
        float wrap = ( clamp( ( ndl + 0.5 ) / 1.5, 0.0, 1.0 ) - clamp( ndl, 0.0, 1.0 ) ) * 0.4;
        reflectedLight.directDiffuse += directionalLights[ 0 ].color * vec3( 1.08, 1.04, 0.72 ) * lit * vTip
          * ( through + wrap ) * BRDF_Lambert( material.diffuseColor );
      }
      #endif`);
  };

  return {
    material, wind,
    shapes: {
      stone: stoneGeometry(),
      stick: stickGeometry(),
      clump: clumpGeometry(),
      log: logGeometry(),
      shell: shellGeometry(),
      bone: boneGeometry(),
      fern: fernGeometry(),
      flower: flowerGeometry(),
      mushroom: mushroomGeometry(),
      moss: mossGeometry(),
      seedhead: seedheadGeometry(),
      carpet: carpetGeometry(),
      bloom: bloomGeometry(),
      seedstalk: seedStalkGeometry(),
      clover: cloverGeometry(),
      leaf: leafGeometry(),
    },
  };
}

export function createNearDetail(): NearDetail {
  const group = new THREE.Group();
  group.name = 'nearDetail';

  const { material, wind, shapes } = litterKit();

  const build = (geo: THREE.BufferGeometry, cap: number, name: string,
                 casts = false): THREE.InstancedMesh => {
    const m = new THREE.InstancedMesh(geo, material, cap);
    m.name = name;
    m.count = 0;
    // one mesh covers a travelling disc; its geometry bounds say otherwise, and
    // culling by them can only ever throw the whole layer away at once
    m.frustumCulled = false;
    m.receiveShadow = true;
    // …and the litter that has a body casts (2026-08-18): stones, clumps,
    // logs, ferns and mushrooms leave a shadow on the ground they stand on,
    // which is most of what "it has weight" means at this range. Sticks,
    // seedheads, flowers, moss and the grass carpet do not — thinner than a
    // shadow texel, they would only add noise to the map — and the wind sway
    // is not in the depth pass, which nothing this size can show.
    m.castShadow = casts;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // setColorAt allocates the colour buffer lazily — do it here, so no update
    // call is ever the one that allocates
    for (let i = 0; i < cap; i++) m.setColorAt(i, WHITE);
    m.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    group.add(m);
    return m;
  };
  const stoneGeo = shapes.stone, stickGeo = shapes.stick;
  const clumpGeo = shapes.clump, logGeo = shapes.log;
  const shellGeo = shapes.shell, boneGeo = shapes.bone;
  const fernGeo = shapes.fern, flowerGeo = shapes.flower;
  const mushGeo = shapes.mushroom, mossGeo = shapes.moss;
  const seedGeo = shapes.seedhead;
  // Indexed by the kind constants in `world/litter.ts`, which is why the four
  // originals keep indices 0–3 there: this array IS that numbering.
  const meshes = [
    build(stoneGeo, CAP_STONE, 'nearStones', true),
    build(stickGeo, CAP_STICK, 'nearSticks'),
    build(clumpGeo, CAP_CLUMP, 'nearClumps', true),
    build(logGeo, CAP_LOG, 'nearLogs', true),
    build(shellGeo, CAP_SHELL, 'nearShells'),
    build(boneGeo, CAP_BONE, 'nearBones'),
    build(fernGeo, CAP_GROWTH_SPECIAL, 'nearFerns', true),
    build(flowerGeo, CAP_GROWTH_SPECIAL, 'nearFlowers'),
    build(mushGeo, CAP_GROWTH_SPECIAL, 'nearMushrooms', true),
    build(mossGeo, CAP_GROWTH_SPECIAL, 'nearMoss'),
    build(seedGeo, CAP_GROWTH_SPECIAL, 'nearSeedheads'),
  ];
  const counts = new Array<number>(KIND_COUNT).fill(0);
  /**
   * The landmark band draws through its own four meshes so it can rebuild on
   * its own cadence: its items live on the 8 m super-lattice, so nothing
   * about its *set* can change until the focus crosses a super-cell — but the
   * dense disc rebuilds every 0.75 m, and while the two shared meshes every
   * disc rebuild paid the band's 49k-super-cell scan again. Measured: half
   * the travelling frame's litter cost, spent recomputing an unchanged band.
   * Four more draw calls, and the band scan runs at an eleventh the rate.
   * Caps are per-band worst cases, measured: forest fringes ~1.7k clumps,
   * alpine ~600 stones, woody country a few hundred logs.
   */
  const bandMeshes = [
    build(stoneGeo, 1800, 'bandStones', true),
    build(stickGeo, 8, 'bandSticks'),
    build(clumpGeo, 2800, 'bandClumps', true),
    build(logGeo, 520, 'bandLogs', true),
  ];
  const bandCounts = [0, 0, 0, 0];
  /** The grass carpet — groundcover, not things; see the constants above. */
  const carpetGeo = shapes.carpet;
  const carpet = build(carpetGeo, CARPET_CAP, 'carpet');
  let carpetCount = 0;
  /** The flora — the carpet's company; see FLORA_SPOTS. Four meshes, four caps. */
  const floraGeo = [shapes.bloom, shapes.seedstalk, shapes.clover, shapes.leaf];
  const floraCap = [CAP_BLOOM, CAP_SEED, CAP_CLOVER, CAP_LEAF];
  const flora = floraGeo.map((g, k) => build(g, floraCap[k], ['bloom', 'seedstalk', 'clover', 'leaf'][k]));
  const floraCount = [0, 0, 0, 0];
  const F_BLOOM = 0, F_SEED = 1, F_CLOVER = 2, F_LEAF = 3;

  // ---- the terrain, as it is actually drawn ------------------------------
  /**
   * The ground under an item is not `groundAt`.
   *
   * `groundAt` is the *continuous* surface; the terrain the eye sees is that
   * function sampled on chunk LOD 0's 4 m lattice and triangulated, and between
   * two vertices it is a flat facet. Measured against the mounted chunks over 60
   * points of hillside, the two disagree by 0.11 m on average and by up to
   * 0.42 m — which quietly buries a 0.2 m pebble whole, and did: the first build
   * of this layer placed three thousand items and showed perhaps thirty.
   *
   * So sample the mesh instead. Chunk vertices sit at multiples of 4 m in world
   * space (`cx * 256 + i * 4`), and everything inside this disc is LOD 0 — the
   * streamer keeps three chunks in every direction there, and the disc reaches
   * 190 m — so the four corners around a point, interpolated across the same
   * diagonal `chunkView.ts` splits its quads along, *are* the surface. As a
   * bonus the facet hands over its exact gradient, which is what tips a stone
   * onto the slope it is lying on.
   *
   * Corners are cached in their own toroidal grid, four times coarser than the
   * item lattice, so this costs about a quarter of the terrain samples the naive
   * version was already paying.
   */
  const MESH = 4;
  const HGRID = 128, HMASK = HGRID - 1;
  const hKx = new Int32Array(HGRID * HGRID);
  const hKz = new Int32Array(HGRID * HGRID);
  const hSet = new Uint8Array(HGRID * HGRID);
  const hY = new Float32Array(HGRID * HGRID);
  const cornerY = (gx: number, gz: number, groundAt: (x: number, z: number) => number): number => {
    const s = ((gz & HMASK) << 7) | (gx & HMASK);
    if (hSet[s] === 0 || hKx[s] !== gx || hKz[s] !== gz) {
      hKx[s] = gx; hKz[s] = gz; hSet[s] = 1;
      hY[s] = groundAt(gx * MESH, gz * MESH);
    }
    return hY[s];
  };
  /** Facet height and gradient at a point, left in these three. */
  let faceY = 0, faceGx = 0, faceGz = 0;
  const sampleFacet = (x: number, z: number, groundAt: (x: number, z: number) => number): void => {
    const gx = Math.floor(x / MESH), gz = Math.floor(z / MESH);
    const u = x / MESH - gx, v = z / MESH - gz;
    const h00 = cornerY(gx, gz, groundAt), h10 = cornerY(gx + 1, gz, groundAt);
    const h01 = cornerY(gx, gz + 1, groundAt), h11 = cornerY(gx + 1, gz + 1, groundAt);
    // chunkView indexes each quad as (00, 01, 10) and (10, 01, 11): the shared
    // diagonal runs 01–10, so u + v ≤ 1 is the first triangle
    let du: number, dv: number;
    if (u + v <= 1) { du = h10 - h00; dv = h01 - h00; faceY = h00 + u * du + v * dv; }
    else { du = h11 - h01; dv = h11 - h10; faceY = h11 + (u - 1) * du + (v - 1) * dv; }
    faceGx = du / MESH; faceGz = dv / MESH;
  };

  // ---- the resolve caches ------------------------------------------------
  // Per lattice cell, the two things that are expensive to learn: the ground
  // height under the item, and what the site there litters with. Both are pure
  // functions of position and neither can ever change — the height field is the
  // seed's, and env/overlay history repaints the ground without moving or
  // re-biomeing it — so a cell resolved once is resolved forever. Two caches
  // of the same shape: one keyed by lattice cell for the dense disc, one keyed
  // by super-cell for the landmark band, which stores its elected cell's item.
  const mkCache = (slots: number) => ({
    kx: new Int32Array(slots),
    kz: new Int32Array(slots),
    /** 0 empty · 1 resolved · 2 resolved, woody country · 3 resolved, carries nothing. */
    state: new Uint8Array(slots),
    /** World generation each slot was resolved under — see `update`. */
    gen: new Uint32Array(slots),
    y: new Float32Array(slots),
    amount: new Uint8Array(slots),
    /** Cumulative kind thresholds, ×255: below t1 stone, below t2 litter, else growth. */
    t1: new Uint8Array(slots),
    t2: new Uint8Array(slots),
    /** The facet's gradient under the item, ×63 — enough for a tilt, two bytes. */
    gx: new Int8Array(slots),
    gz: new Int8Array(slots),
    /** How pale this ground's litter should be, ×200 — see `biomeTone`. */
    tone: new Uint8Array(slots),
    /** How packed by living, ×255 — sweeps the carpet, thins the clumps. */
    worn: new Uint8Array(slots),
    /**
     * What this ground is known for, as a kind index + 1 so that 0 is "nothing"
     * and the array can stay a byte. Decided in `world/litter.ts` on its own
     * 24 m lattice; cached per 2 m cell because that is what this cache is
     * keyed by, and re-deriving it per cell per rebuild would cost two hashes
     * on the hottest loop in the layer for an answer that cannot change.
     */
    special: new Uint8Array(slots),
    /**
     * …and how strongly it takes over, as the `SPECIAL_P` CODE the world module
     * hands out — 0, 1 or 2, stored and returned unchanged.
     *
     * It was `round(specialP × 255)` and that was a pointer/picture
     * disagreement: the renderer then specialised against 158/255 = 0.619608
     * where `litterAt` used 0.62, so the cells whose `S_SPECIAL` hash landed in
     * the sliver between them were drawn as a clump and named as a fern —
     * 40 of 182,477 items. A code cannot round.
     */
    strength: new Uint8Array(slots),
  });
  type Cache = ReturnType<typeof mkCache>;
  const near = mkCache(SLOTS);
  const land = mkCache(FSLOTS);
  let lastGen = 0;

  // ---- per-frame scratch: the update path allocates nothing ---------------
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
  const _tip = new THREE.Quaternion(), _n = new THREE.Vector3(), _up = new THREE.Vector3(0, 1, 0);
  const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color();

  let builtX = 0, builtZ = 0, builtR = -1, builtT = -1, built = false;
  /** The band's own rebuild key: the super-cell under the focus, and its fade. */
  let bandSX = 0, bandSZ = 0, bandFade = -1, bandBuilt = false;
  /** The cells whose item is drawn this rebuild — disc and band, kept apart because they rebuild apart. */
  const discDrawn = new Set<number>(), bandDrawn = new Set<number>();
  /** The settings menu's live multipliers — see the interface note. */
  const sc = { reach: 1, grass: 1 };

  /**
   * Learn one cell into one cache. Called once per cell, ever — the payload
   * never expires. `keyX/keyZ` is the slot's own key (the cell for the dense
   * disc, the super-cell for the landmark band); `ix/iz` is the lattice cell
   * whose item is being learned.
   */
  const resolve = (
    c: Cache, slot: number, keyX: number, keyZ: number, ix: number, iz: number,
    groundAt: (x: number, z: number) => number,
    siteAt: (x: number, z: number) => { land: boolean; biome: string; rain: number },
    displaced?: (x: number, z: number) => boolean,
    worn?: (x: number, z: number) => number,
  ): void => {
    const px = spotX(ix, iz), pz = spotZ(ix, iz);
    c.kx[slot] = keyX; c.kz[slot] = keyZ; c.gen[slot] = lastGen;
    // carried off: the ground the baseline grew it on is bare now
    if (displaced && displaced(px, pz)) { c.state[slot] = 3; return; }
    const site = siteAt(px, pz);
    if (!site.land) { c.state[slot] = 3; return; }
    sampleFacet(px, pz, groundAt);
    if (faceY < DRY_FOOT) { c.state[slot] = 3; return; }
    // What this ground is made of is a question about the world, so it is
    // answered in `world/litter.ts` — by the same call the pointer makes. The
    // cache below holds the answer in bytes; it does not decide it.
    const cell = litterCell(site, px, pz);
    if (!cell) { c.state[slot] = 3; return; }
    const bio = BIOMES[site.biome as BiomeKey] as Biome | undefined;

    c.worn[slot] = worn ? Math.round(clamp(worn(px, pz), 0, 1) * 255) : 0;
    c.y[slot] = faceY;
    c.gx[slot] = Math.round(clamp(faceGx, -2, 2) * 63);
    c.gz[slot] = Math.round(clamp(faceGz, -2, 2) * 63);
    c.amount[slot] = Math.round(cell.amount * 255);
    c.tone[slot] = Math.round(biomeTone(bio) * 200);
    c.t1[slot] = cell.t1;
    c.t2[slot] = cell.t2;
    c.special[slot] = cell.special + 1;
    c.strength[slot] = cell.strength;
    c.state[slot] = cell.woody ? 2 : 1;
  };

  /**
   * The final kind of one cell — the whole of the decision, in one place.
   *
   * Both passes call it, so a fern the disc draws and a clump the band decides
   * it owns cannot be the same cell seen two ways. Everything it reads is
   * either a hash of `(ix, iz)` or a byte the cache learned from
   * `world/litter.ts`; nothing here decides anything the world did not.
   *
   * The order matters and is the world module's, not this one's: base kind,
   * then the log substitution, then occupancy (which is where `wornKeep`
   * lands), and only then the speciality. See `litterAt`.
   */
  const kindOf = (c: Cache, slot: number, ix: number, iz: number, woody: boolean): number => {
    const hk = hash01(ix, iz, S_KIND) * 255;
    let k = hk < c.t1[slot] ? K_STONE : hk < c.t2[slot] ? K_STICK : K_CLUMP;
    if (k === K_STICK && woody && hash01(ix, iz, S_LOG) < LOG_CHANCE) k = K_LOG;
    return k;
  };

  /** Instances drawn by the current disc rebuild — HARD_CAP's ledger. */
  let total = 0;
  /** …and by the current band rebuild, against its own smaller cap. */
  let bandTotal = 0;
  const BAND_HARD_CAP = 4600;

  /**
   * Compose one item into its set's mesh — both passes draw through here, so
   * a landmark log and a disc log are the same log down to the last hash.
   * `t` is the item's growth: budget fade, rim fade, or landmark fade,
   * whichever pass owns it.
   */
  const drawItem = (
    set: THREE.InstancedMesh[], setCounts: number[],
    kind: number, ix: number, iz: number, y: number, gx: number, gz: number,
    tone200: number, t: number, groundAt: (x: number, z: number) => number,
  ): boolean => {
    const i = setCounts[kind];
    const mesh = set[kind];
    if (i >= mesh.instanceMatrix.count) return false;

    const px = spotX(ix, iz), pz = spotZ(ix, iz);
    const h1 = hash01(ix, iz, S_SZ), h2 = hash01(ix, iz, S_SH);
    const h3 = hash01(ix, iz, S_ROT), h4 = hash01(ix, iz, S_TONE);
    const yaw = h3 * TAU;
    /**
     * How much of the ground's own tone this kind takes — 1 for everything
     * that is a piece of the ground, less for the one kind that is not. See
     * `TONE_FLOWER`.
     */
    let toneMix = 1;
    const cure = clamp(dryness.value, 0, 1);

    switch (kind) {
      case K_STONE: {
        // a pebble, occasionally a fist of stone. Bedded a tenth of its
        // height into the facet it stands on — the placement is exact now,
        // so this is a seat rather than the insurance it used to be. The
        // bury rides the fade, or a vanishing pebble would climb out of
        // the earth as it shrank.
        const w = litterSize(K_STONE, h1, h2) * t;
        const hgt = w * lerp(0.42, 0.95, h2);
        _p.set(px, y - hgt * 0.12, pz);
        _n.set(-gx * 0.8 + (h4 - 0.5) * 0.24, 1, -gz * 0.8 + (h1 - 0.5) * 0.24).normalize();
        _e.set(0, yaw, 0);
        _tip.setFromEuler(_e);
        _q.setFromUnitVectors(_up, _n).multiply(_tip);
        _s.set(w * lerp(0.8, 1.2, h4), hgt, w * lerp(0.8, 1.2, h2));
        _m.compose(_p, _q, _s);
        // cool grey, warmed where the ground is dry, paled on stony biomes
        const grey = lerp(0.24, 0.41, h4);
        _c.setRGB(grey * 1.02, grey, grey * 1.10);
        _c.lerp(EARTH, (1 - h2) * 0.3);
        break;
      }
      case K_STICK: {
        // fatter than a true twig on purpose: a stick one pixel wide is a
        // bright scratch, not a stick, and a hundred scratches is noise
        const len = litterSize(K_STICK, h1, h2) * t;
        const rad = lerp(0.021, 0.048, h2) * MODEL * t;
        _p.set(px, y - rad * 0.4, pz);
        // lies down the slope it happens to be on, give or take. The +x
        // end points along (cos yaw, 0, −sin yaw), so the ground's rise
        // along the stick is gx·cos − gz·sin, and a Z pitch of that much
        // lays it flat (order YZX ⇒ R = Ry·Rz).
        _e.set(0, yaw, clamp(gx * Math.cos(yaw) - gz * Math.sin(yaw), -0.5, 0.5)
                       + (h4 - 0.5) * 0.16, 'YZX');
        _q.setFromEuler(_e);
        _s.set(len, rad, rad);
        _m.compose(_p, _q, _s);
        _c.copy(WOOD).lerp(STRAW, h4 * 0.7);
        break;
      }
      case K_CLUMP: {
        // the widest thing in the layer, and the one that carries it: a
        // low broad mass merges with its neighbours into the drifts the
        // patch fields lay out, where a narrow one would only stipple
        const w = litterSize(K_CLUMP, h1, h2) * t;
        const hgt = w / lerp(1.15, 2.1, h2);
        _p.set(px, y - hgt * 0.05, pz);
        _e.set(0, yaw, 0);
        _q.setFromEuler(_e);
        _s.set(w, hgt, w);
        _m.compose(_p, _q, _s);
        {
          // A clump is a tussock, so it cures with the carpet it sits in. Read
          // here rather than passed down: this runs once per placed item, not
          // per blade, and it is the same shared object the ground uses, so the
          // two cannot say different things about the month.
          const f = h4 * 0.85 + (1 - h2) * 0.15;
          _c.copy(SEDGE).lerp(HAY, f + (1 - f) * cure * 0.92);
        }
        break;
      }
      case K_SHELL: {
        // seated on the facet exactly as a pebble is, because that is what a
        // shell on a strand does — the tilt is what stops a field of them
        // reading as buttons on a tablecloth
        const w = litterSize(K_SHELL, h1, h2) * t;
        const hgt = w * lerp(0.30, 0.50, h2);
        _p.set(px, y - hgt * 0.10, pz);
        _n.set(-gx * 0.7 + (h4 - 0.5) * 0.30, 1, -gz * 0.7 + (h1 - 0.5) * 0.30).normalize();
        _e.set(0, yaw, 0);
        _tip.setFromEuler(_e);
        _q.setFromUnitVectors(_up, _n).multiply(_tip);
        _s.set(w, hgt, w);
        _m.compose(_p, _q, _s);
        _c.copy(SHELL_PALE).lerp(SHELL_WARM, h4 * 0.8);
        break;
      }
      case K_BONE: {
        // lies down the slope on the stick's own arithmetic — same convention,
        // same geometry orientation, so the two cannot drift apart
        const len = litterSize(K_BONE, h1, h2) * t;
        const rad = len * lerp(0.045, 0.075, h2);
        _p.set(px, y - rad * 0.35, pz);
        _e.set(0, yaw, clamp(gx * Math.cos(yaw) - gz * Math.sin(yaw), -0.5, 0.5)
                       + (h4 - 0.5) * 0.12, 'YZX');
        _q.setFromEuler(_e);
        _s.set(len, rad, rad);
        _m.compose(_p, _q, _s);
        // weathered rather than white: a bone that has lain out is the colour
        // of the dust around it, with the pale only on what still faces the sky
        _c.copy(BONE_PALE).lerp(EARTH, (1 - h4) * 0.34);
        break;
      }
      case K_FERN: {
        const w = litterSize(K_FERN, h1, h2) * t;
        const hgt = w * lerp(0.62, 0.90, h2);
        _p.set(px, y - hgt * 0.03, pz);
        _e.set(0, yaw, 0);
        _q.setFromEuler(_e);
        _s.set(w, hgt, w);
        _m.compose(_p, _q, _s);
        // a fern browns off in a dry year, but far less than a tussock does —
        // it is in the shade and it has a root ball
        _c.copy(FROND).lerp(SEDGE, h4 * 0.45).lerp(HAY, cure * 0.30);
        break;
      }
      case K_FLOWER: {
        const w = litterSize(K_FLOWER, h1, h2) * t;
        const hgt = w * lerp(1.20, 1.80, h2);
        _p.set(px, y, pz);
        _e.set(0, yaw, 0);
        _q.setFromEuler(_e);
        _s.set(w, hgt, w);
        _m.compose(_p, _q, _s);
        // the hue is the whole point, so it is picked and NOT blended toward a
        // neighbour's: a meadow whose flowers all lerp between two colours is
        // one flower in a gradient
        _c.copy(BLOOM[Math.min(BLOOM.length - 1, Math.floor(h4 * BLOOM.length))]);
        // …and it only takes a third of the ground's tone, or a red flower on a
        // coast-forest floor is a brown lump
        toneMix = TONE_FLOWER;
        break;
      }
      case K_MUSHROOM: {
        const w = litterSize(K_MUSHROOM, h1, h2) * t;
        const hgt = w * lerp(1.00, 1.50, h2);
        _p.set(px, y - hgt * 0.04, pz);
        _e.set(0, yaw, 0);
        _q.setFromEuler(_e);
        _s.set(w, hgt, w);
        _m.compose(_p, _q, _s);
        _c.copy(CAP_BROWN).lerp(CAP_IVORY, h4);
        break;
      }
      case K_MOSS: {
        const w = litterSize(K_MOSS, h1, h2) * t;
        const hgt = w * lerp(0.16, 0.28, h2);
        _p.set(px, y - hgt * 0.20, pz);
        _e.set(0, yaw, 0);
        _q.setFromEuler(_e);
        _s.set(w, hgt, w);
        _m.compose(_p, _q, _s);
        // the one green in the layer that does NOT cure: moss lives where the
        // ground stays wet, and a brown cushion is a dead one
        _c.copy(MOSS_GREEN).lerp(SEDGE, h4 * 0.40);
        break;
      }
      case K_SEEDHEAD: {
        const w = litterSize(K_SEEDHEAD, h1, h2) * t;
        const hgt = w * lerp(1.60, 2.30, h2);
        _p.set(px, y, pz);
        _e.set(0, yaw, 0);
        _q.setFromEuler(_e);
        _s.set(w, hgt, w);
        _m.compose(_p, _q, _s);
        _c.copy(SEED_DRY).lerp(STRAW, h4 * 0.7 + cure * 0.3);
        break;
      }
      default: {
        const len = litterSize(K_LOG, h1, h2) * t;
        const dia = lerp(0.16, 0.36, h2) * MODEL * t;
        // a log is long enough that the ground under its two ends decides
        // how it lies — the one item here worth sampling the facets its
        // ends rest on rather than the one under its middle
        const ex = Math.cos(yaw) * len * 0.5, ez = -Math.sin(yaw) * len * 0.5;
        sampleFacet(px + ex, pz + ez, groundAt);
        const ya = faceY;
        sampleFacet(px - ex, pz - ez, groundAt);
        const yb = faceY;
        _p.set(px, (ya + yb) * 0.5 - dia * 0.18, pz);
        _e.set(0, yaw, clamp(Math.atan2(ya - yb, len), -0.55, 0.55), 'YZX');
        _q.setFromEuler(_e);
        _s.set(len, dia, dia);
        _m.compose(_p, _q, _s);
        _c.copy(WOOD).lerp(STRAW, h4 * 0.45);
        break;
      }
    }
    _c.multiplyScalar((0.86 + h3 * 0.28) * lerp(1, tone200 / 200, toneMix));
    mesh.setMatrixAt(i, _m);
    mesh.setColorAt(i, _c);
    setCounts[kind] = i + 1;
    return true;
  };

  /** Per-pass tallies of the last rebuild, for probes — group.userData.stats. */
  const stats: { disc: number[]; band: number[]; carpet: number; flora: number[]; rebuildMs: number } = {
    disc: new Array<number>(KIND_COUNT).fill(0),
    band: [0, 0, 0, 0],
    carpet: 0,
    flora: [0, 0, 0, 0],
    /** Wall time of the last disc rebuild — the carpet and flora are most of it. */
    rebuildMs: 0,
  };
  group.userData.stats = stats;

  return {
    group,
    update(tSec, focusX, focusZ, camR, groundAt, siteAt, displaced, worldGen = 0, carried, worn, water) {
      /*
       * The year, on the grass under your feet.
       *
       * §32 cured the ground and the standing plants through a shader uniform,
       * because both are baked into meshes that would otherwise have to be
       * rebuilt continuously. This layer is the exception that needs no
       * uniform: it is coloured on the CPU and it ALREADY rebuilds once a world
       * day (`worldGen` folds `clock.t / 86400`), which at 192× is every 7.5
       * real minutes — and dryness moves about a hundredth per world day, so
       * daily steps are far finer than the eye can catch.
       *
       * Read once per update rather than per blade: the carpet is up to ~15k
       * clusters and this sits inside that loop.
       *
       * It also needs no new colour. The carpet has always lerped SEDGE → HAY
       * by a hash, so the hay is already in the palette and the season only has
       * to push the blend along it — which preserves every blade's existing
       * variation instead of flattening the field to one tone.
       */
      const cure = clamp(dryness.value, 0, 1);
      // the sway runs whether or not anything is rebuilt: it is the one thing
      // here that moves, and it moves in the shader
      wind.value = tSec;

      if (camR >= FAR * sc.reach) {
        if (built || bandBuilt) {
          for (let k = 0; k < meshes.length; k++) { meshes[k].count = 0; counts[k] = 0; }
          for (let k = 0; k < bandMeshes.length; k++) {
            bandMeshes[k].count = 0; bandCounts[k] = 0;
          }
          carpet.count = 0;
          carpetCount = 0;
          for (let k = 0; k < flora.length; k++) { flora[k].count = 0; floraCount[k] = 0; }
          built = false;
          bandBuilt = false;
          discDrawn.clear(); bandDrawn.clear();
        }
        group.visible = false;
        return;
      }
      group.visible = true;

      // something in the world moved: every cached cell has to be asked again,
      // because the disc keys its cache on cell index and a stone that was
      // lifted has the same index it always had
      if (worldGen !== lastGen) { lastGen = worldGen; built = false; bandBuilt = false; }

      const R = lerp(R_NEAR, R_FAR, smoothstep(CAM_TIGHT, CAM_WIDE, camR)) * sc.reach;
      // the landmark band's altitude fade — the one number of its rebuild key
      // that can move while the focus stands still
      const fFade = 1 - smoothstep(FAR * sc.reach * 0.72, FAR * sc.reach, camR);

      // ---- the landmark band, on its own cadence -------------------------
      // Its items live on the 8 m super-lattice, so its set cannot change
      // until the focus crosses a super-cell; per-item fades drift under one
      // percent inside a crossing. Rebuilt at an eleventh the disc's rate,
      // which is what makes a 49k-super-cell scan affordable during travel.
      const sx0 = Math.floor(focusX / (STEP * SUPER));
      const sz00 = Math.floor(focusZ / (STEP * SUPER));
      if (!(bandBuilt && sx0 === bandSX && sz00 === bandSZ
            && Math.abs(fFade - bandFade) < 0.02)) {
        bandSX = sx0; bandSZ = sz00; bandFade = fFade; bandBuilt = true;
        bandTotal = 0;
        bandCounts[0] = bandCounts[1] = bandCounts[2] = bandCounts[3] = 0;
        stats.band.fill(0);
        bandDrawn.clear();
        if (fFade > 0.02) {
          const S8 = STEP * SUPER;
          const LB = LFAR * sc.reach;
          const fHalf = Math.ceil(LB / S8);
          const szq0 = Math.floor(focusZ / S8);
          const LR2 = LB * LB, lInner = LB - LRIM;
          for (let jz = -fHalf; jz <= fHalf; jz++) {
            const szc = szq0 + jz;
            const czm = (szc + 0.5) * S8 - focusZ;
            const span2 = LR2 - czm * czm;
            if (span2 <= 0) continue;
            const span = Math.sqrt(span2);
            const from = Math.ceil((focusX - span) / S8 - 0.5);
            const to = Math.floor((focusX + span) / S8 - 0.5);
            const row = (szc & FMASK) * FGRID;
            for (let sx = from; sx <= to; sx++) {
              const cxm = (sx + 0.5) * S8 - focusX;
              const d2 = cxm * cxm + czm * czm;
              if (d2 > LR2) continue;

              const ix = repX(sx, szc), iz = repZ(sx, szc);
              const slot = row + (sx & FMASK);
              if (land.state[slot] === 0 || land.kx[slot] !== sx || land.kz[slot] !== szc
                  || land.gen[slot] !== lastGen) {
                resolve(land, slot, sx, szc, ix, iz, groundAt, siteAt, displaced, worn);
              }
              const st = land.state[slot];
              if (st === 3) continue;

              const base = kindOf(land, slot, ix, iz, st === 2);
              const h1 = hash01(ix, iz, S_SZ), h2 = hash01(ix, iz, S_SH);
              // existence at full budget — the same item `litterAt` reports —
              // with the living things swept off walked ground by the world's
              // own rule, read from `world/litter.ts` rather than restated here
              const occAmt = (land.amount[slot] / 255) * BOOST[base]
                * wornKeep(base, land.worn[slot] / 255);
              if (hash01(ix, iz, S_OCC) >= occAmt) continue;
              // …and only then the ground's speciality, in the world's order.
              // A special is never a band item, so this can only ever REMOVE a
              // cell from the band — which is exactly what the disc's ownership
              // test below concludes about the same cell, from the same call.
              const kind = specialise(base, land.special[slot] - 1,
                                      land.strength[slot], ix, iz);
              if (!farKeeps(kind, h1, h2)) continue;

              // an item carries as far as its size can pay for, then shrinks
              const dist = Math.sqrt(d2);
              const need = dist / FAR_REACH[kind];
              const t = fFade * smoothstep(LB, lInner, dist)
                * smoothstep(need * 0.55, need, farSize(kind, h1, h2));
              if (t < 0.02 || bandTotal >= BAND_HARD_CAP) continue;

              if (drawItem(bandMeshes, bandCounts, kind, ix, iz, land.y[slot],
                           land.gx[slot] / 63, land.gz[slot] / 63,
                           land.tone[slot], t, groundAt)) {
                stats.band[kind]++;
                bandTotal++;
                bandDrawn.add(cellId(ix, iz));
              }
            }
          }
        }
        for (let k = 0; k < 4; k++) {
          const m = bandMeshes[k];
          m.count = bandCounts[k];
          m.visible = bandCounts[k] > 0;
          m.instanceMatrix.needsUpdate = true;
          if (m.instanceColor) m.instanceColor.needsUpdate = true;
        }
      }

      // ---- the dense disc ------------------------------------------------
      // Rebuild only when it can show. Every item stands still in the world,
      // so between rebuilds there is nothing to recompute: an orbit around a
      // fixed focus costs this module a single uniform write. Three quarters
      // of a metre is a twentieth of the rim fade — smaller than one frame's
      // step of any fade this layer performs.
      if (built && Math.abs(R - builtR) < 1.2
          && Math.abs(focusX - builtX) < 0.75 && Math.abs(focusZ - builtZ) < 0.75) return;
      // …and not every frame while the eye is flying (2026-08-19): a rebuild
      // is 13–19 ms of main-thread work (measured at the close station: 19k
      // blades, 26k flora, 2.4k litter), and at any speed the focus crosses
      // 0.75 m a frame, so an unthrottled disc cost a third of the frame
      // budget for the whole flight. Forty milliseconds between rebuilds
      // lags the disc by a metre at a fast flight's 25 m/s, under the 26 m
      // rim fade's notice — unless the focus has JUMPED (a glide, a
      // Chronicle flight, a station), when the hole would show and the
      // rebuild is immediate.
      const jumped = Math.abs(focusX - builtX) > 6 || Math.abs(focusZ - builtZ) > 6;
      if (built && !jumped && tSec - builtT < 0.04) return;
      builtX = focusX; builtZ = focusZ; builtR = R; builtT = tSec; built = true;

      const half = Math.ceil(R / STEP);
      const iz0 = Math.floor(focusZ / STEP);
      const R2 = R * R, rInner = Math.max(1, R - RIM);

      /**
       * Solve the acceptance threshold for the budget. Cells in the disc is
       * geometry; what a cell yields is the biome's business, read once at the
       * focus and clamped, so the threshold is smooth as the focus travels and
       * the count still rises in rich country and falls in poor. Reading the
       * whole disc instead would make the budget lurch at every biome edge.
       */
      const fSpec = SPEC[siteAt(focusX, focusZ).biome] ?? FALLBACK;
      const cells = Math.PI * R2 / (STEP * STEP);
      const keep = clamp(TARGET * sc.reach / (cells * clamp(fSpec.amount, A_REF_LO, A_MAX)), 0, 1);

      // the carpet's own budget, solved the same way against the growth the
      // focus's ground yields — a meadow carries a thick pile, a scree a thin
      // one, and the count never runs away in either
      const CR = Math.min(R, CARPET_R * sc.grass);
      const CR2 = CR * CR, crInner = Math.max(1, CR - CARPET_RIM);
      const carpetFade = sc.grass <= 0.01 ? 0
        : 1 - smoothstep(CARPET_NEAR * sc.grass, CARPET_FAR * sc.grass, camR);
      const growthF = fSpec.growth / Math.max(1e-4, fSpec.stone + fSpec.litter + fSpec.growth);
      const keepC = carpetFade <= 0.02 ? 0 : clamp(
        Math.min(CARPET_TARGET * sc.grass, CARPET_CAP)
          / (Math.PI * CR2 / (STEP * STEP) * CARPET_SPOTS
          * clamp(fSpec.amount * growthF, 0.2, 1)), 0, 1);

      const rebuildT0 = performance.now();
      total = 0;
      counts.fill(0);
      carpetCount = 0;
      floraCount.fill(0);
      stats.disc.fill(0);
      discDrawn.clear();

      for (let jz = -half; jz <= half; jz++) {
        const iz = iz0 + jz;
        const cz = (iz + 0.5) * STEP - focusZ;
        const span2 = R2 - cz * cz;
        if (span2 <= 0) continue;
        // scanline the disc: the corners of the bounding square are never visited
        const span = Math.sqrt(span2);
        const from = Math.ceil((focusX - span) / STEP - 0.5);
        const to = Math.floor((focusX + span) / STEP - 0.5);
        const row = (iz & GMASK) * GRID;
        for (let ix = from; ix <= to; ix++) {
          const cxm = (ix + 0.5) * STEP - focusX;
          const d2 = cxm * cxm + cz * cz;
          if (d2 > R2) continue;

          const slot = row + (ix & GMASK);
          if (near.state[slot] === 0 || near.kx[slot] !== ix || near.kz[slot] !== iz
              || near.gen[slot] !== lastGen) {
            resolve(near, slot, ix, iz, ix, iz, groundAt, siteAt, displaced, worn);
          }
          const st = near.state[slot];
          if (st === 3) continue;

          // ---- the grass carpet: up to four blade-clusters per cell ------
          // Groundcover rides the growth this cell's ground already yields;
          // it is drawn alongside whatever litter stands here and asks the
          // budget, the facet and nothing else. Blades shrink away at their
          // own rim and sink entirely as the eye rises past CARPET_FAR.
          if (keepC > 0 && d2 < CR2) {
            // packed ground is swept ground: grass will not stand where
            // people do, and the square keeps the camp's edge a gradient
            // …and no grass stands in a river: the one thing this layer asks
            // another renderer, once per cell, so the carpet and its flora
            // stop at the drawn bank while the bed keeps its stones
            const swept = (1 - near.worn[slot] / 255)
              * (water && water((ix + 0.5) * STEP, (iz + 0.5) * STEP) ? 0 : 1);
            // …and grass grows in COMPANY (2026-08-18): the same budget, gathered
            // into tussocks and openings by a smooth field at tussock scale (7 m)
            // instead of spread one blade-cluster per two metres over the whole
            // disc, which is a carpet in the wrong sense — a texture, not a
            // meadow. The field's mean is ~1, so the disc holds as many blades
            // as it did; they simply stand together. World-anchored: the same
            // tussock is in the same place on every visit (law 1, kept).
            const cellX = (ix + 0.5) * STEP, cellZ = (iz + 0.5) * STEP;
            const tussock = 0.22 + 1.6 * smoothstep(0.36, 0.68,
              valueNoise(cellX / 7.0, cellZ / 7.0, S_TUSSOCK) * 0.7
              + valueNoise(cellX / 2.6, cellZ / 2.6, S_TUSSOCK + 1) * 0.3);
            // the carpet takes the SQUARE ROOT of the cell's litter amount:
            // `openness` puts a third of the world's litter near its floor
            // (glades of bare earth, which is right for sticks and stones) but
            // groundcover does not open the way deadfall does — a meadow with a
            // third of its ground bare is not a meadow. Softened, not removed:
            // packed camp floors and burnt ground still sweep it (`swept`, t2).
            const pBase = keepC * Math.sqrt(near.amount[slot] / 255) * (1 - near.t2[slot] / 255)
              * swept * swept;
            const pC = pBase * tussock;
            if (pC > 0.004) {
              const rimC = smoothstep(CR, crInner, Math.sqrt(d2)) * carpetFade;
              for (let s = 0; s < CARPET_SPOTS && rimC > 0.03; s++) {
                const sx4 = ix * CARPET_SPOTS + s;
                const grow = (pC - hash01(sx4, iz, S_COCC)) / 0.12;
                if (grow <= 0) continue;
                const t = Math.min(1, grow) * rimC;
                if (t < 0.03 || carpetCount >= CARPET_CAP) continue;
                // a 4 × 3 lattice of jittered spots inside the cell
                const px = (ix + (s % 4) * 0.25 + hash01(sx4, iz, S_CX) * 0.25) * STEP;
                const pz = (iz + Math.floor(s / 4) * (1 / 3) + hash01(sx4, iz, S_CZ) * (1 / 3)) * STEP;
                sampleFacet(px, pz, groundAt);
                const h1 = hash01(sx4, iz, S_CSZ), h4 = hash01(sx4, iz, S_CTONE);
                const hgt = lerp(0.20, 0.60, h1) * t;
                _p.set(px, faceY - hgt * 0.05, pz);
                _e.set(0, h4 * TAU, 0);
                _q.setFromEuler(_e);
                _s.set(hgt * lerp(1.1, 1.9, h4), hgt, hgt * lerp(1.1, 1.9, h1));
                _m.compose(_p, _q, _s);
                {
                  const f = h4 * 0.75 + (1 - h1) * 0.1;
                  _c.copy(SEDGE).lerp(HAY, f + (1 - f) * cure * 0.92);
                }
                // a shade lighter than it was (2026-08-18): with a dark base
                // baked into the blade and the tips lit through, the cluster's
                // own tint can sit nearer the ground's, or a meadow reads as
                // a scatter of dark marks on a lit slope
                _c.multiplyScalar((0.98 + h1 * 0.34) * (near.tone[slot] / 200));
                carpet.setMatrixAt(carpetCount, _m);
                carpet.setColorAt(carpetCount, _c);
                carpetCount++;
              }
            }
            // ---- the flora: the carpet's company ----
            // Same budget word (`keepC`), same rim and fade, same sweep — and
            // each kind asks one more question of the ground before it stands:
            // blooms want a drift, stalks want a tussock, clover wants a patch,
            // leaves want litter country. Each kind has its own spots a cell
            // and its own cap; see FLORA_SPOTS.
            if (pBase > 0.004 || near.t2[slot] > near.t1[slot]) {
              const rimC = smoothstep(CR, crInner, Math.sqrt(d2)) * carpetFade;
              if (rimC > 0.03) {
                const litterShare = Math.max(0, near.t2[slot] - near.t1[slot]) / 255;
                const woody = st === 2;
                const drift = smoothstep(0.48, 0.68,
                  valueNoise(cellX / 5.5, cellZ / 5.5, S_DRIFT) * 0.72
                  + valueNoise(cellX / 1.8, cellZ / 1.8, S_DRIFT + 1) * 0.28);
                const patch = smoothstep(0.50, 0.70, valueNoise(cellX / 3.5, cellZ / 3.5, S_PATCH));
                const leafP = smoothstep(0.30, 0.62, valueNoise(cellX / 4.0, cellZ / 4.0, S_LEAFP));
                const tone = near.tone[slot] / 200;
                // a drift is one colour: the 11 m cell it lies in picks the hue
                const driftHue = Math.floor(hash01(Math.floor(cellX / 11), Math.floor(cellZ / 11), S_DRIFT_HUE) * CARPET_BLOOM.length);
                const pk = [
                  pBase * (0.06 + 1.3 * drift),                                    // blooms: a few anywhere, thick in a drift
                  pBase * (0.12 + 0.40 * Math.min(1.8, tussock) / 1.8),            // seed stalks
                  pBase * (0.04 + 1.2 * patch),                                    // clover: patches, a pad or two anywhere
                  // leaves: litter country only, thickest under the wood — the
                  // cap is spent where a forest floor is, not a meadow's sticks
                  keepC * swept * smoothstep(0.12, 0.5, litterShare) * (woody ? 2.2 : 0.5) * leafP,
                ];
                for (let kind = 0; kind < 4; kind++) {
                  const p = pk[kind];
                  if (p <= 0.004) continue;
                  const spots = FLORA_SPOTS[kind];
                  const mesh = flora[kind], cap = floraCap[kind];
                  for (let s = 0; s < spots; s++) {
                    if (floraCount[kind] >= cap) break;
                    const sx = ix * 16 + s, sz = iz * 4 + kind;
                    const grow = (p - hash01(sx, sz, S_FOCC)) / 0.12;
                    if (grow <= 0) continue;
                    const t = Math.min(1, grow) * rimC;
                    if (t < 0.03) continue;
                    // jittered spots on a lattice the kind's own size
                    const cols = kind === F_BLOOM ? 4 : kind === F_LEAF ? 3 : 2;
                    const rows = Math.ceil(spots / cols);
                    const px = (ix + (s % cols) / cols + hash01(sx, sz, S_FX) / cols) * STEP;
                    const pz = (iz + Math.floor(s / cols) / rows + hash01(sx, sz, S_FZ) / rows) * STEP;
                    sampleFacet(px, pz, groundAt);
                    const h1 = hash01(sx, sz, S_FS), h4 = hash01(sx, sz, S_FT);
                    switch (kind) {
                      case F_BLOOM: {
                        // a fifth of a drift's blooms are another colour
                        const hue = h4 < 0.2 ? Math.floor(h1 * CARPET_BLOOM.length) : driftHue;
                        const hgt = lerp(0.22, 0.44, h1) * t;
                        _p.set(px, faceY - hgt * 0.03, pz);
                        _e.set(0, h4 * TAU, 0);
                        _q.setFromEuler(_e);
                        _s.set(hgt, hgt, hgt);
                        _m.compose(_p, _q, _s);
                        // brighter than the litter's flower: a carpet bloom is a
                        // spot in a field, and a spot that takes the ground's
                        // tone is a spot that vanishes into it
                        _c.copy(CARPET_BLOOM[hue % CARPET_BLOOM.length]).multiplyScalar(0.9 + h1 * 0.2);
                        break;
                      }
                      case F_SEED: {
                        const hgt = lerp(0.34, 0.72, h1) * t;
                        _p.set(px, faceY - hgt * 0.03, pz);
                        _e.set(0, h4 * TAU, 0);
                        _q.setFromEuler(_e);
                        _s.set(hgt * 0.8, hgt, hgt * 0.8);
                        _m.compose(_p, _q, _s);
                        _c.copy(HAY).lerp(SEED_DRY, 0.4 + cure * 0.5).multiplyScalar((0.95 + h4 * 0.35) * tone);
                        break;
                      }
                      case F_CLOVER: {
                        const w = lerp(0.16, 0.32, h1) * t;
                        _p.set(px, faceY + 0.006, pz);
                        _n.set(-faceGx, 1, -faceGz).normalize();
                        _e.set(0, h4 * TAU, 0);
                        _tip.setFromEuler(_e);
                        _q.setFromUnitVectors(_up, _n).multiply(_tip);
                        _s.set(w, w * 0.35, w);
                        _m.compose(_p, _q, _s);
                        _c.copy(CLOVER).lerp(HAY, cure * 0.5).multiplyScalar((0.9 + h4 * 0.3) * tone);
                        break;
                      }
                      default: {
                        const w = lerp(0.08, 0.17, h1) * t;
                        _p.set(px, faceY + 0.004, pz);
                        _n.set(-faceGx, 1, -faceGz).normalize();
                        _e.set(0, h4 * TAU, 0);
                        _tip.setFromEuler(_e);
                        _q.setFromUnitVectors(_up, _n).multiply(_tip);
                        _s.set(w, w * 0.5, w * lerp(0.8, 1.2, h4));
                        _m.compose(_p, _q, _s);
                        _c.copy(LEAF_FALL[Math.floor(h4 * LEAF_FALL.length) % LEAF_FALL.length]).multiplyScalar((0.85 + h1 * 0.4) * tone);
                        break;
                      }
                    }
                    mesh.setMatrixAt(floraCount[kind], _m);
                    mesh.setColorAt(floraCount[kind], _c);
                    floraCount[kind]++;
                  }
                }
              }
            }
          }

          // kind first, because how hard each kind fights for the budget differs
          const base = kindOf(near, slot, ix, iz, st === 2);
          // …and the speciality straight after it, because the band's ownership
          // test has to be asked about the SAME kind the band asked about, and
          // the band asks after specialising. One cell, one owner, one answer.
          const kind = specialise(base, near.special[slot] - 1,
                                  near.strength[slot], ix, iz);

          // the landmark band owns its elected cells' big items — one owner
          // per item, so a log never fades at a rim its landmark self ignores
          const sx = Math.floor(ix / SUPER), szc = Math.floor(iz / SUPER);
          if (ix === repX(sx, szc) && iz === repZ(sx, szc)
              && farKeeps(kind, hash01(ix, iz, S_SZ), hash01(ix, iz, S_SH))) continue;

          // occupancy is priced on the BASE kind, as `litterAt` prices it: a
          // speciality stands where its channel's ordinary item would have, so
          // a flush of mushrooms is exactly as dense as the clumps it replaced.
          // The living things thin where the ground is walked and the dead
          // debris stays, because a camp's floor has debris and no grass —
          // `wornKeep` is the world's own rule, not a second copy of it.
          const p = keep * (near.amount[slot] / 255) * BOOST[base]
            * wornKeep(base, near.worn[slot] / 255);
          const grow = (p - hash01(ix, iz, S_OCC)) / KEEP_FADE;
          if (grow <= 0) continue;
          // two soft edges, multiplied: the budget's, and the rim of the disc
          const t = Math.min(1, grow) * smoothstep(R, rInner, Math.sqrt(d2));
          if (t < 0.02 || total >= HARD_CAP) continue;

          if (drawItem(meshes, counts, kind, ix, iz, near.y[slot],
                       near.gx[slot] / 63, near.gz[slot] / 63,
                       near.tone[slot], t, groundAt)) {
            stats.disc[kind]++;
            total++;
            discDrawn.add(cellId(ix, iz));
          }
        }
      }

      // ---- and the things a god has moved (Design IV §21) ----------------
      // They are not on the lattice any more — that is the whole point of
      // having been carried — so they are appended straight into the same
      // per-kind meshes. Few enough that a linear pass is free, and drawn at
      // their real size so the stone you put down is the stone you picked up.
      if (carried) {
        for (const c of carried) {
          const kind = KIND_INDEX[c.kind] ?? K_STONE;
          const mesh = meshes[kind];
          const i = counts[kind];
          if (i >= mesh.instanceMatrix.count) continue;
          if ((c.x - focusX) ** 2 + (c.z - focusZ) ** 2 > R2) continue;
          sampleFacet(c.x, c.z, groundAt);
          const w = c.size * (kind === K_CLUMP ? 0.5 : 1);
          _p.set(c.x, faceY, c.z);
          _e.set(0, hash01(Math.round(c.x), Math.round(c.z), S_ROT) * TAU, 0);
          _q.setFromEuler(_e);
          _s.set(w, w * (kind === K_STONE ? 0.7 : 0.5), w);
          _m.compose(_p, _q, _s);
          mesh.setMatrixAt(i, _m);
          _c.copy(EARTH).lerp(WHITE, 0.35);
          mesh.setColorAt(i, _c);
          counts[kind] = i + 1;
          discDrawn.add(cellId(Math.floor(c.x / STEP), Math.floor(c.z / STEP)));
        }
      }

      for (let k = 0; k < meshes.length; k++) {
        const m = meshes[k];
        m.count = counts[k];
        m.visible = counts[k] > 0;
        m.instanceMatrix.needsUpdate = true;
        if (m.instanceColor) m.instanceColor.needsUpdate = true;
      }
      carpet.count = carpetCount;
      carpet.visible = carpetCount > 0;
      carpet.instanceMatrix.needsUpdate = true;
      if (carpet.instanceColor) carpet.instanceColor.needsUpdate = true;
      stats.carpet = carpetCount;
      for (let k = 0; k < flora.length; k++) {
        const m = flora[k];
        m.count = floraCount[k];
        m.visible = floraCount[k] > 0;
        m.instanceMatrix.needsUpdate = true;
        if (m.instanceColor) m.instanceColor.needsUpdate = true;
        stats.flora[k] = floraCount[k];
      }
      stats.rebuildMs = performance.now() - rebuildT0;
    },
    isDrawn(x, z) {
      const id = cellId(Math.floor(x / STEP), Math.floor(z / STEP));
      return discDrawn.has(id) || bandDrawn.has(id);
    },
    setScale(reach, grass) {
      const r = clamp(reach, 0, 4), g = clamp(grass, 0, 4);
      if (r === sc.reach && g === sc.grass) return;
      sc.reach = r; sc.grass = g;
      built = false; bandBuilt = false;
    },
    dispose() {
      // the band shares the disc's four geometries, so those dispose once
      for (const m of [...meshes, ...bandMeshes, carpet, ...flora]) {
        group.remove(m);
        m.dispose();
      }
      for (const g of [stoneGeo, stickGeo, clumpGeo, logGeo, carpetGeo,
                       shellGeo, boneGeo, fernGeo, flowerGeo, mushGeo, mossGeo, seedGeo]) {
        g.dispose();
      }
      material.dispose();
    },
  };
}
